(() => {
  const ui = (text) => window.SBUI ? window.SBUI(text) : text;
  const localeRoute = (route) => {
    const root = document.documentElement.dataset.sbLocaleRoot || '/';
    return (root === '/es' || root === '/es/' ? '/es' : '') + route;
  };
  const root = document.documentElement;
  const crossStoreEnabled = root.dataset.sbCrossStoreEnabled === 'true';
  const cartRequest = (url, options) => crossStoreEnabled ? (window.SBStoreB ? window.SBStoreB.request(url, options) : Promise.reject(new Error('Availability could not be checked. Please refresh.'))) : window.fetch(url, options);
  const crossStoreMessages = JSON.parse(document.querySelector('#SBCrossStoreCheckout')?.textContent || '{}').messages || {};
  let pendingCartChanges = 0;
  let checkoutInProgress = false;
  const openCheckout = async (cart, nativeUrl) => {
    if (!crossStoreEnabled) { window.location.assign(nativeUrl); return; }
    if (pendingCartChanges || checkoutInProgress) throw new Error(crossStoreMessages.pending);
    if (!window.SBCrossStoreCheckout) throw new Error(crossStoreMessages.failed);
    checkoutInProgress = true;
    try { await window.SBStoreB.open(cart); }
    finally { checkoutInProgress = false; }
  };
  const subscriptionsEnabled = root.dataset?.sbSubscriptionsEnabled === 'true';
  const subscriptionNotice = () => root.dataset?.sbSubscriptionNotice || 'Subscriptions are temporarily unavailable. Choose a one-time purchase.';
  const subscriptionCartNotice = () => root.dataset?.sbSubscriptionCartNotice || 'Subscriptions are temporarily unavailable. Remove subscription items and add them again as one-time purchases to continue.';
  const liveRegion = document.querySelector('#sb-live-region');
  const cartDrawer = document.querySelector('#StarsBoosterCartDrawer');
  let cartReturnFocus = null;
  let contentModalReturnFocus = null;
  let predictiveController = null;
  let cartChannel = null;

  try {
    if ('BroadcastChannel' in window) cartChannel = new BroadcastChannel('starsbooster-cart');
  } catch (_) {
    cartChannel = null;
  }

  const announce = (message) => {
    if (liveRegion) liveRegion.textContent = message;
  };

  const formatMoney = (minor, currency) => {
    if (window.SBDisplayMoney) return window.SBDisplayMoney.format(minor, currency || 'EUR');
    const amount = Number(minor || 0);
    const usesWholeUnits = amount % 100 === 0;
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: currency || 'EUR',
      minimumFractionDigits: usesWholeUnits ? 0 : 2,
      maximumFractionDigits: usesWholeUnits ? 0 : 2
    }).format(amount / 100);
  };

  // Product totals are stored in minor units. Keep fractional unit prices exact
  // (for example, 10% off EUR 15 is EUR 13.50, never a rounded EUR 14).
  const formatProductMoney = formatMoney;

  const currentCurrency = () => document.querySelector('meta[property="product:price:currency"]')?.content || 'EUR';

  const openCart = (trigger) => {
    if (!cartDrawer) return;
    cartReturnFocus = trigger || document.activeElement;
    cartDrawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('sb-drawer-open');
    document.querySelectorAll('[data-sb-cart-open]').forEach((button) => button.setAttribute('aria-expanded', 'true'));
    cartDrawer.querySelector('[data-sb-cart-close]')?.focus();
  };

  const closeCart = () => {
    if (!cartDrawer) return;
    cartDrawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('sb-drawer-open');
    document.querySelectorAll('[data-sb-cart-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
    cartReturnFocus?.focus?.();
  };

  const createElement = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  const cartItemProperties = (item) => item.properties && typeof item.properties === 'object' ? item.properties : {};
  const cartItemIsAddon = (item) => ['Gig cost', 'Add-on', 'Priced add-on'].some((key) => Boolean(cartItemProperties(item)[key]));
  const cartItemBundleId = (item) => String(cartItemProperties(item)['_StarsBooster bundle'] || '');
  const cartItemBundle = (item) => cartItemBundleId(item) || item.key;
  const cartItemHandle = (item) => String(item.handle || item.product_handle || item.url?.match(/\/products\/([^?]+)/)?.[1] || '');
  const hasPausedSubscriptions = (cart) => !subscriptionsEnabled && (cart?.items || []).some((item) => Boolean(item.selling_plan_allocation) || cartItemHandle(item) === 'monthly-review-removal-plans');
  const syncSubscriptionCartState = (cart) => {
    const blocked = hasPausedSubscriptions(cart) || (crossStoreEnabled && cart.items.some(item => item.available === false));
    document.querySelectorAll('[data-sb-subscription-cart-alert]').forEach((alert) => { alert.hidden = !blocked; });
    document.querySelectorAll('[data-sb-cart-page]').forEach((form) => {
      form.dataset.sbSubscriptionsBlocked = String(blocked);
      const checkout = form.querySelector('[name="checkout"]');
      if (checkout && !checkout.hasAttribute('aria-busy')) checkout.disabled = blocked || !cart.items.length || analyzeCart(cart).orphanItems.length > 0;
    });
  };
  const assertCheckoutAvailable = (cart) => {
    if (crossStoreEnabled && cart.items.some(item => item.available === false)) throw new Error('A selected package or add-on is unavailable. Please remove or change it.');
    if (hasPausedSubscriptions(cart)) throw new Error(subscriptionCartNotice());
    if (analyzeCart(cart).orphanItems.length) throw new Error('Your cart contains incomplete service selections. Add the affected service again to repair the cart before checkout, or contact support.');
  };
  const cartItemOptionValues = (item) => {
    const values = Array.isArray(item.options_with_values)
      ? item.options_with_values.map((option) => ({ name: String(option.name || ''), value: String(option.value || '') }))
      : (Array.isArray(item.variant_options) ? item.variant_options.map((value) => ({ name: '', value: String(value || '') })) : []);
    return values.filter(({ value }) => value && value !== 'Default Title');
  };
  const cartItemPackSize = (item) => {
    const options = cartItemOptionValues(item);
    const quantityOption = options.find(({ name }) => /quantity|quanity|quanitly|reviews?|removals?|package/i.test(name));
    const candidates = [quantityOption?.value, item.variant_title].filter(Boolean).map(String);
    for (const value of candidates) {
      const multiplied = value.match(/[×x]\s*(\d+)/i);
      if (multiplied) return Math.max(1, Number(multiplied[1]));
      if (value === quantityOption?.value || /reviews?|removals?|^\s*\d+\s*\//i.test(value)) {
        const leading = value.match(/^\s*(\d+)/);
        if (leading) return Math.max(1, Number(leading[1]));
      }
    }
    return 1;
  };
  const cartItemMetric = (item) => {
    const handle = cartItemHandle(item);
    const count = cartItemPackSize(item) * Math.max(1, Number(item.quantity || 1));
    if (handle === 'google-deindexing-packages') return { kind: 'service', count, singular: 'URL', plural: 'URLs' };
    if (handle.includes('removal')) return { kind: 'removal', count, singular: 'removal', plural: 'removals' };
    if (/setup|traffic|review-like|likes/.test(handle)) return { kind: 'service', count: Math.max(1, Number(item.quantity || 1)), singular: 'service', plural: 'services' };
    return { kind: 'review', count, singular: 'review', plural: 'reviews' };
  };
  const cartMetricText = (metric) => `${metric.count} ${ui(metric.count === 1 ? metric.singular : metric.plural)}`;
  const cartLineOriginal = (item) => {
    const original = Number(item.original_line_price ?? item.final_line_price ?? 0);
    const allocation = item.selling_plan_allocation;
    const comparison = allocation?.compare_at_price;
    const quantity = Number(item.quantity);
    if (typeof comparison !== 'number' || !Number.isFinite(comparison) || comparison < 0
      || !Number.isSafeInteger(quantity) || quantity < 1) return original;
    return Math.max(original, comparison * quantity);
  };
  const analyzeCart = (cart) => {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const primaryItems = items.filter((item) => !cartItemIsAddon(item));
    const primaryByBundle = new Map();
    primaryItems.forEach((item) => {
      const bundle = cartItemBundleId(item);
      if (!bundle) return;
      const group = primaryByBundle.get(bundle) || [];
      group.push(item);
      primaryByBundle.set(bundle, group);
    });
    const addonItems = items.filter(cartItemIsAddon);
    const linkedItems = addonItems.filter((item) => {
      const owners = primaryByBundle.get(cartItemBundleId(item));
      return owners?.length === 1;
    });
    const orphanItems = addonItems.filter((item) => !linkedItems.includes(item));
    const visibleItems = [...primaryItems, ...linkedItems];
    const totalFor = (field) => visibleItems.reduce((sum, item) => sum + (field === 'original_line_price' ? cartLineOriginal(item) : Number(item[field] ?? item.final_line_price ?? 0)), 0);
    const metrics = primaryItems.reduce((result, item) => {
      const metric = cartItemMetric(item);
      result[metric.kind] += metric.count;
      return result;
    }, { review: 0, removal: 0, service: 0 });
    const originalTotal = totalFor('original_line_price');
    const finalTotal = totalFor('final_line_price');
    return {
      primaryItems,
      linkedItems,
      orphanItems,
      metrics,
      originalTotal,
      finalTotal,
      totalDiscounts: Math.max(0, originalTotal - finalTotal)
    };
  };
  const cartMetaText = (analysis) => {
    const count = analysis.primaryItems.length;
    const parts = [`${count} ${ui(count === 1 ? 'order' : 'orders')}`];
    if (analysis.metrics.review) parts.push(`${analysis.metrics.review} ${ui(analysis.metrics.review === 1 ? 'review' : 'reviews')}`);
    if (analysis.metrics.removal) parts.push(`${analysis.metrics.removal} ${ui(analysis.metrics.removal === 1 ? 'removal' : 'removals')}`);
    if (analysis.metrics.service) parts.push(`${analysis.metrics.service} ${ui(analysis.metrics.service === 1 ? 'service' : 'services')}`);
    return parts.join(' · ');
  };
  const applyCartCount = (cart) => {
    const count = analyzeCart(cart).primaryItems.length;
    const countText = String(count);
    document.querySelectorAll('[data-sb-cart-count]').forEach((node) => {
      node.textContent = countText;
      node.dataset.sbCartCountSize = countText.length >= 3 ? 'wide' : 'compact';
      node.hidden = count === 0;
    });
  };

  const syncCartCount = async () => {
    try {
      const response = await cartRequest(localeRoute('/cart.js'), {
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return null;
      const cart = await response.json();
      window.__starsBoosterCart = cart;
      applyCartCount(cart);
      syncSubscriptionCartState(cart);
      return cart;
    } catch (_) {
      return null;
    }
  };

  const notifyCartMutation = () => {
    try { cartChannel?.postMessage({ type: 'cart:changed' }); } catch (_) { /* no-op */ }
  };

  const appendCartStar = (target) => {
    target.innerHTML = '<svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="m12 2.8 2.75 5.58 6.16.9-4.46 4.34 1.05 6.13L12 16.85l-5.5 2.9 1.05-6.13L3.1 9.28l6.15-.9L12 2.8Z"/></svg>';
  };

  const cartItemOptionSummary = (item) => {
    const properties = cartItemProperties(item);
    const contentValue = String(properties['Content option'] || '').toLowerCase();
    const isProvidedContent = contentValue.includes('provide');
    const content = isProvidedContent
      ? "I'll Provide Content"
      : (contentValue ? 'Write It for Me' : '');
    const ratingValue = String(properties['Star rating'] || '');
    const rating = Number.parseInt(ratingValue, 10);
    const gender = !isProvidedContent ? String(properties['Reviewer gender'] || '') : '';
    const options = cartItemOptionValues(item);
    const optionByName = (pattern) => options.find(({ name }) => pattern.test(name))?.value || '';
    const optionByValue = (pattern) => options.find(({ value }) => pattern.test(value))?.value || '';
    const format = String(properties['Feedback format'] || optionByName(/feedback|format/i) || optionByValue(/^text(?:\s*\+\s*(?:image|video))?$/i));
    const pace = String(properties['Campaign pace'] || optionByName(/campaign|pace|speed/i) || optionByValue(/^(?:slow|balanced|faster)$/i));
    const purchase = item.selling_plan_allocation?.selling_plan?.name ? `${item.selling_plan_allocation.selling_plan.name} subscription` : 'One-time';
    const values = [cartMetricText(cartItemMetric(item)), purchase, content, Number.isFinite(rating) ? `${rating}★` : '', gender, format, pace].filter(Boolean);
    return values.join(' · ');
  };
  const cartBundleItems = (item, cart) => {
    const bundle = cartItemBundleId(item);
    if (!bundle || cartItemIsAddon(item)) return [item];
    return [item, ...cart.items.filter((candidate) => cartItemIsAddon(candidate) && cartItemBundleId(candidate) === bundle)];
  };
  const cartBundleTotal = (item, cart, field) => cartBundleItems(item, cart).reduce((sum, candidate) => sum + (field === 'original_line_price' ? cartLineOriginal(candidate) : Number(candidate[field] ?? candidate.final_line_price ?? 0)), 0);
  let cartPlatformAssets = {};
  try { cartPlatformAssets = JSON.parse(document.querySelector('[data-sb-cart-platform-assets]')?.textContent || '{}'); } catch (_) { cartPlatformAssets = {}; }
  const cartItemPlatformKey = (item) => {
    const handle = cartItemHandle(item);
    if (handle.includes('trustpilot')) return 'trustpilot';
    if (handle.includes('tripadvisor')) return 'tripadvisor';
    if (handle.includes('yelp')) return 'yelp';
    if (handle.includes('facebook')) return 'facebook';
    if (handle.includes('fiverr')) return 'fiverr';
    if (handle.includes('booking')) return 'booking';
    if (handle.includes('amazon')) return 'amazon';
    if (handle.includes('capterra')) return 'capterra';
    if (/(^|-)g2(-|$)/.test(handle)) return 'g2';
    if (handle.includes('airbnb')) return 'airbnb';
    if (handle.includes('product-hunt')) return 'product-hunt';
    if (handle.includes('thumbtack')) return 'thumbtack';
    if (handle.includes('glassdoor')) return 'glassdoor';
    return 'google';
  };
  const cartPlatformLabels = {
    google: 'Google Maps', trustpilot: 'Trustpilot', tripadvisor: 'Tripadvisor', yelp: 'Yelp', facebook: 'Facebook',
    fiverr: 'Fiverr', booking: 'Booking', amazon: 'Amazon', capterra: 'Capterra', g2: 'G2', airbnb: 'Airbnb',
    'product-hunt': 'Product Hunt', thumbtack: 'Thumbtack', glassdoor: 'Glassdoor'
  };
  const cartItemTiming = (item) => {
    const properties = cartItemProperties(item);
    const explicit = properties['Start time'] || properties['Delivery timing'] || properties['Delivery time'];
    if (explicit) return String(explicit);
    const handle = cartItemHandle(item);
    if (handle === 'monthly-review-removal-plans') return 'Monthly service';
    if (handle.includes('removal')) return 'Assessment within 72 hours';
    return 'Starts within 48 hours';
  };
  const normalizeCartDetailLabel = (label) => {
    const value = String(label || '').trim();
    if (/^(?:quanity|quanitly|quantity)$/i.test(value)) return 'Quantity';
    if (/^content option$/i.test(value)) return 'Review-writing / content';
    if (/^(?:feedback format|format|media type)$/i.test(value)) return 'Media / content type';
    if (/^business or listing url$/i.test(value)) return 'Business / Profile URL';
    if (/^gig cost$/i.test(value)) return 'Gig cost choice';
    if (/^add-on$/i.test(value)) return 'Reviewer preference';
    if (/^priced add-on$/i.test(value)) return 'Paid cost choice';
    return value || 'Selection';
  };
  const cartItemCustomerDetails = (item, cart) => {
    const rows = [];
    const add = (label, value) => {
      const cleanLabel = normalizeCartDetailLabel(label);
      const cleanValue = String(value ?? '').trim();
      if (!cleanValue || cleanValue === 'Default Title') return;
      const existing = rows.find((row) => row.label === cleanLabel);
      if (!existing) rows.push({ label: cleanLabel, value: cleanValue });
      else if (!existing.value.split(' · ').includes(cleanValue)) existing.value += ` · ${cleanValue}`;
    };
    const platformKey = cartItemPlatformKey(item);
    const metric = cartItemMetric(item);
    add('Platform', cartPlatformLabels[platformKey]);
    add(metric.kind === 'removal' ? 'Removal count' : metric.kind === 'review' ? 'Review count' : 'Service quantity', cartMetricText(metric));
    add('Purchase', item.selling_plan_allocation?.selling_plan?.name ? `${item.selling_plan_allocation.selling_plan.name} subscription` : 'One-time purchase');
    add('Start / delivery timing', cartItemTiming(item));
    cartItemOptionValues(item).forEach(({ name, value }) => {
      if (/quantity|quanity|quanitly/i.test(name)) return;
      add(name || 'Option', value);
    });
    Object.entries(cartItemProperties(item)).forEach(([key, value]) => {
      if (!value || key.startsWith('_') || key === 'Order content') return;
      add(key, value);
    });
    cartBundleItems(item, cart).filter((candidate) => candidate.key !== item.key).forEach((candidate) => {
      const properties = Object.entries(cartItemProperties(candidate)).filter(([key, value]) => value && !key.startsWith('_'));
      properties.forEach(([key, value]) => add(key, value));
      const costLabel = properties.some(([key]) => key === 'Gig cost')
        ? 'Gig cost total'
        : properties.some(([key]) => key === 'Add-on') ? 'Reviewer preference cost' : 'Paid cost total';
      add(costLabel, formatMoney(candidate.final_line_price, cart.currency));
    });
    return rows;
  };

  const cartChangeButton = (item, quantity, label, className = '') => {
    const button = createElement('button', className, label);
    button.type = 'button';
    button.dataset.sbCartChange = item.key;
    button.dataset.sbCartBundle = cartItemBundle(item);
    button.dataset.quantity = String(quantity);
    return button;
  };

  const renderCartItem = (item, cart, orderIndex) => {
    const article = createElement('article', 'sb-cart-order');
    article.dataset.sbCartOrder = '';
    article.dataset.sbCartBundle = cartItemBundle(item);
    const summary = createElement('div', 'sb-cart-order__summary');
    const icon = createElement('span', 'sb-cart-order__icon');
    icon.setAttribute('aria-hidden', 'true');
    const platformAsset = cartPlatformAssets[cartItemPlatformKey(item)];
    if (platformAsset) {
      const image = document.createElement('img');
      image.src = platformAsset;
      image.alt = '';
      image.width = 28;
      image.height = 28;
      icon.append(image);
    } else appendCartStar(icon);

    const identity = createElement('button', 'sb-cart-order__identity');
    identity.type = 'button';
    identity.dataset.sbCartExpand = '';
    identity.setAttribute('aria-expanded', 'false');
    const metric = cartItemMetric(item);
    const metricCount = metric.count;
    identity.append(
      createElement('small', '', `${ui('ORDER')} ${orderIndex + 1}`),
      createElement('strong', '', item.product_title),
      createElement('span', '', cartItemOptionSummary(item))
    );
    const price = createElement('strong', 'sb-cart-order__price', formatMoney(cartBundleTotal(item, cart, 'final_line_price'), cart.currency));
    const edit = createElement('a', 'sb-cart-order__edit', ui('Edit'));
    edit.href = item.url;
    const remove = cartChangeButton(item, 0, '', 'sb-cart-order__remove');
    remove.setAttribute('aria-label', `Remove ${item.product_title}`);
    remove.innerHTML = '<svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M4 7h16M9 7V4h6v3m-8 0 1 13h8l1-13M10 11v5m4-5v5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    const expand = createElement('button', 'sb-cart-order__expand');
    expand.type = 'button';
    expand.dataset.sbCartExpand = '';
    expand.setAttribute('aria-expanded', 'false');
    expand.setAttribute('aria-label', `Show ${item.product_title} order details`);
    expand.innerHTML = '<svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="m6 9 6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    summary.append(icon, identity, price, edit, remove, expand);

    const details = createElement('div', 'sb-cart-order__details');
    details.dataset.sbCartDetails = '';
    details.hidden = true;
    const list = document.createElement('dl');
    const original = cartBundleTotal(item, cart, 'original_line_price');
    const final = cartBundleTotal(item, cart, 'final_line_price');
    const discount = Math.max(0, original - final);
    const detail = (label, value, className = '') => {
      const row = document.createElement('div');
      row.append(createElement('dt', '', ui(label)), createElement('dd', className, value));
      return row;
    };
    list.append(
      detail(ui('Original'), formatMoney(original, cart.currency)),
      detail(ui('Discount'), discount > 0 ? `−${formatMoney(discount, cart.currency)}` : ui('No discount'), discount > 0 ? 'is-saving' : ''),
      detail(metric.kind === 'removal' ? 'Per removal' : metric.kind === 'review' ? 'Per review' : 'Per service', formatMoney(Math.round(final / Math.max(1, metricCount)), cart.currency)),
      detail(ui('Discount rate'), discount > 0 && original > 0 ? `${Math.round((discount / original) * 100)}% off` : '—')
    );
    cartItemCustomerDetails(item, cart).forEach(({ label, value }) => {
      const row = detail(label, value);
      row.className = 'sb-cart-order__property';
      list.append(row);
    });
    const actions = createElement('div', 'sb-cart-order__detail-actions');
    const editOrder = createElement('a', '', ui('Edit order'));
    editOrder.href = item.url;
    if (metric.kind === 'review') {
      const quantities = createElement('div', 'sb-cart-order__quantities');
      quantities.setAttribute('aria-label', 'Review quantity');
      const packSize = cartItemPackSize(item);
      [1, 5, 10, 20, 50, 100].filter((quantity) => quantity % packSize === 0).forEach((quantity) => {
        const button = cartChangeButton(item, quantity, String(quantity));
        button.dataset.sbCartTargetReviews = String(quantity);
        if (metricCount === quantity) button.setAttribute('aria-current', 'true');
        quantities.append(button);
      });
      if (quantities.childElementCount) actions.append(quantities);
    }
    actions.append(editOrder);
    details.append(list, actions);
    if (crossStoreEnabled && cartBundleItems(item, cart).some(line => line.available === false)) {
      identity.append(createElement('span', 'sb-cart-integrity-alert', ui('Unavailable')));
    }
    article.append(summary, details);
    return article;
  };

  const refreshCartDrawer = async () => {
    for (const cartSurface of [cartDrawer, document.querySelector('[data-sb-b-cart-page]')].filter(Boolean)) {
    cartSurface.setAttribute('aria-busy', 'true');
    try {
      const response = await cartRequest(localeRoute('/cart.js'), { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Cart is temporarily unavailable.');
      const cart = await response.json();
      window.__starsBoosterCart = cart;
      applyCartCount(cart);
      syncSubscriptionCartState(cart);
      const body = cartSurface.querySelector('[data-sb-cart-body]');
      const analysis = analyzeCart(cart);
      const mainItems = analysis.primaryItems;
      const meta = cartSurface.querySelector('[data-sb-cart-meta]');
      const subtotal = cartSurface.querySelector('[data-sb-cart-subtotal]');
      const discounts = cartSurface.querySelector('[data-sb-cart-discounts]');
      const total = cartSurface.querySelector('[data-sb-cart-total]');
      if (meta) meta.textContent = cartMetaText(analysis);
      if (subtotal) subtotal.textContent = formatMoney(analysis.originalTotal, cart.currency);
      if (discounts) discounts.textContent = `−${formatMoney(analysis.totalDiscounts, cart.currency)}`;
      if (total) total.textContent = formatMoney(analysis.finalTotal, cart.currency);
      const footer = cartSurface.querySelector('[data-sb-cart-footer]');
      if (footer) footer.hidden = mainItems.length === 0;
      cartSurface.dataset.sbCartIntegrity = analysis.orphanItems.length ? 'invalid' : 'valid';
      if (!body) continue;
      body.replaceChildren();
      if (crossStoreEnabled && cart.items.some(item => item.available === false)) {
        const alert = createElement('p', 'sb-cart-integrity-alert', ui('A package or add-on is unavailable. Remove or change the marked order to continue.'));
        alert.setAttribute('role', 'alert'); body.append(alert);
      }
      if (hasPausedSubscriptions(cart)) {
        const notice = createElement('div', 'sb-cart-integrity-alert', subscriptionCartNotice());
        notice.setAttribute('role', 'alert');
        notice.dataset.sbSubscriptionCartAlert = '';
        body.append(notice);
      }
      if (analysis.orphanItems.length) {
        const integrity = createElement('div', 'sb-cart-integrity-alert', ui('Your cart contains incomplete service selections. Add the affected service again to repair the cart before checkout, or contact support.'));
        integrity.setAttribute('role', 'alert');
        body.append(integrity);
      }
      if (!mainItems.length) {
        const empty = createElement('div', 'sb-cart-empty');
        const star = createElement('span', '', '★');
        star.setAttribute('aria-hidden', 'true');
        empty.append(star, createElement('strong', '', ui('Your cart is empty')), createElement('p', '', ui('Choose a service to get started.')));
        body.append(empty);
      } else {
        const orders = createElement('div', 'sb-cart-orders');
        mainItems.forEach((item, index) => orders.append(renderCartItem(item, cart, index)));
        body.append(orders);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Cart is temporarily unavailable.';
      const body = cartSurface.querySelector('[data-sb-cart-body]');
      if (body) body.textContent = message;
      cartSurface.querySelectorAll('[name="checkout"], [data-sb-cart-checkout]').forEach(button => { button.disabled = true; });
      announce(message);
    } finally {
      cartSurface.removeAttribute('aria-busy');
    }
    }
  };

  const updateCartBundle = async (trigger) => {
    let cart = window.__starsBoosterCart;
    if (!cart) {
      const cartResponse = await cartRequest(localeRoute('/cart.js'), { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error('Unable to update this cart item.');
      cart = await cartResponse.json();
    }
    const item = cart.items.find((candidate) => candidate.key === trigger.dataset.sbCartChange);
    if (!item) throw new Error('Unable to update this cart item.');
    const requested = Math.max(0, Number(trigger.dataset.quantity || 0));
    const packSize = cartItemPackSize(item);
    let nextServiceQuantity = requested;
    if (trigger.dataset.sbCartTargetReviews !== undefined) {
      if (requested > 0 && requested % packSize !== 0) {
        window.location.assign(item.url);
        return null;
      }
      nextServiceQuantity = requested === 0 ? 0 : requested / packSize;
    }
    const group = cartBundleItems(item, cart);
    const updates = {};
    group.forEach((candidate) => {
      if (candidate.key === item.key) updates[candidate.key] = nextServiceQuantity;
      else updates[candidate.key] = nextServiceQuantity === 0 ? 0 : Math.max(1, Math.round((Number(candidate.quantity || 1) / Math.max(1, Number(item.quantity || 1))) * nextServiceQuantity));
    });
    return cartRequest(localeRoute('/cart/update.js'), {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });
  };

  const closeMobileMenu = () => {
    const menu = document.querySelector('#StarsBoosterMobileNav');
    const button = document.querySelector('[data-sb-menu-toggle]');
    if (menu) menu.hidden = true;
    if (button) button.setAttribute('aria-expanded', 'false');
  };

  const selectedProductVariant = (form) => {
    const source = form.querySelector('[data-sb-product-variants]');
    if (!source) return null;
    let variants = [];
    try { variants = JSON.parse(source.textContent || '[]'); } catch { return null; }
    const selections = [...form.querySelectorAll('[data-sb-product-option]')].map((group) => group.querySelector('input:checked')?.value || '');
    return variants.find((item) => item.options?.every((value, index) => value === selections[index])) || variants.find((item) => String(item.id) === form.querySelector('[data-sb-variant-id]')?.value) || null;
  };

  const selectedReviewCount = (form) => {
    const rawValue = form.querySelector('[data-sb-option-name="Choose quantity"] input:checked')?.value || form.querySelector('input[name="quantity"]')?.value || '1';
    const parsedValue = Number.parseInt(String(rawValue), 10);
    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 1;
  };

  const formatFigmaSummaryMoney = (amount, currency = 'EUR') => {
    if (window.SBDisplayMoney?.currency === 'USD') return window.SBDisplayMoney.format(amount, currency);
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount)) return '';
    const symbol = new Intl.NumberFormat('en', {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).formatToParts(0).find((part) => part.type === 'currency')?.value || currency;
    return `${symbol}${Math.round(numericAmount / 100)}`;
  };

  const nominalMoneyFromLabel = (value) => {
    const match = String(value || '').replace(/\s/g, '').match(/[0-9]+(?:[.,][0-9]{1,2})?/);
    if (!match) return Number.NaN;
    const parsed = Number(match[0].replace(',', '.'));
    return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed * 100) : Number.NaN;
  };

  const enforceCustomerMedia = (form, variant) => {
    const media = (variant?.options || []).some((option) => /image|video/i.test(String(option)));
    const writing = form.querySelector('[data-sb-written-content]');
    const writtenChoice = writing?.querySelector('input');
    const providedChoice = form.querySelector('[data-sb-content-choice="provide"]');
    if (writing) { writing.hidden = media; writing.style.display = media ? 'none' : ''; }
    if (writtenChoice) writtenChoice.disabled = media;
    if (media && providedChoice) providedChoice.checked = true;
    const help = form.querySelector('[data-sb-dashboard-media-help]');
    if (help) help.hidden = !media;
    const business = form.querySelector('[data-sb-content-business-value]');
    if (business && media) business.disabled = false;
    const instructions = form.querySelector('[data-sb-writing-instructions]');
    if (instructions) { instructions.hidden = providedChoice?.checked === true; instructions.querySelectorAll('textarea').forEach((input) => { input.disabled = instructions.hidden; }); }
    return media;
  };

  const updateProductPricing = (form, suppliedVariant = null) => {
    const variant = suppliedVariant || selectedProductVariant(form);
    if (!variant) return;
    enforceCustomerMedia(form, variant);
    const count = Math.max(1, selectedReviewCount(form));
    const customCostInput = form.querySelector('[data-sb-custom-cost]');
    const costRule = window.SBCheckoutSourceRules?.[String(variant.id)]?.costRule;
    let costQuote = null, costSelection = null, costError = '';
    if (customCostInput) {
      try {
        if (!crossStoreEnabled || !costRule || !window.SBCustomCost) throw Error('The cost calculator is loading. Please try again.');
        if (!window.SBCostInput) throw Error('The cost calculator is loading. Please try again.');
        costSelection = window.SBCostInput.read(customCostInput, count, costRule.feeBasisPoints);
        costQuote = costSelection.quote;
      } catch (error) { costError = error.message; }
      customCostInput.setAttribute('aria-invalid', costError ? 'true' : 'false');
      customCostInput.setCustomValidity(costError);
      const feedback = form.querySelector('[data-sb-custom-cost-error]');
      if (feedback) { feedback.hidden = !costError; feedback.textContent = costError; }
    }
    const sellingPlan = form.querySelector('input[name="selling_plan"]:checked');
    const isSubscription = subscriptionsEnabled && Boolean(sellingPlan?.value);
    const subscriptionPercent = isSubscription ? Math.max(0, Number(sellingPlan?.dataset.sbSellingDiscount || 0)) : 0;
    const addon = form.querySelector('[data-sb-linked-addon]:checked:not(:disabled)');
    const addonUnitPrice = Number((isSubscription ? addon?.dataset.sbLinkedAllocationPrice : addon?.dataset.sbAddonPrice) || 0);
    const addonPrice = addonUnitPrice * count;
    const formatChoice = form.querySelector('[data-sb-option-name="Feedback format"] input:checked');
    const formatUnitPrice = crossStoreEnabled ? (window.SBStoreB?.quote(variant.id).media || 0) / count : Number(formatChoice?.dataset.sbFormatPrice || 0);
    const formatPrice = formatUnitPrice * count;
    const gigGroup = form.querySelector('[data-sb-gig-selector]');
    const gig = form.querySelector('[data-sb-gig-variant]:checked:not(:disabled)');
    const gigUnitPrice = Number((isSubscription ? gig?.dataset.sbLinkedAllocationPrice : gig?.dataset.price) || 0);
    const gigPrice = costRule?.kind === 'fiverr' && costQuote ? costQuote.total : gigUnitPrice * count;
    const gigNominalUnitPrice = costRule?.kind === 'fiverr' && costQuote ? costQuote.unitCostCents : nominalMoneyFromLabel(gig?.dataset.sbGigLabel || '');
    const gigNominalPrice = costRule?.kind === 'fiverr' && costQuote ? costQuote.cost : Number.isFinite(gigNominalUnitPrice) ? gigNominalUnitPrice * count : 0;
    const gigFeeUnitPrice = Number.isFinite(gigNominalUnitPrice) ? gigUnitPrice - gigNominalUnitPrice : 0;
    const gigFeePrice = costRule?.kind === 'fiverr' && costQuote ? costQuote.fee : gigFeeUnitPrice * count;
    const pricedAddonGroup = form.querySelector('[data-sb-priced-addon-group]');
    const pricedAddon = form.querySelector('[data-sb-priced-addon]:checked:not(:disabled)');
    const pricedAddonKind = costRule?.kind !== 'fiverr' && customCostInput ? costRule?.kind : pricedAddon?.dataset.sbAddonKind || pricedAddonGroup?.dataset.sbPricedAddonKind || '';
    const pricedAddonUnitPrice = Number((isSubscription ? pricedAddon?.dataset.sbLinkedAllocationPrice : pricedAddon?.dataset.price) || 0);
    const pricedAddonPrice = pricedAddonKind && costQuote ? costQuote.total : pricedAddonUnitPrice * (pricedAddon?.dataset.sbAddonQuantity ? 1 : count);
    const pricedAddonNominalUnitPrice = pricedAddonKind && costQuote ? costQuote.unitCostCents : nominalMoneyFromLabel(pricedAddon?.value || '');
    const pricedAddonNominalPrice = pricedAddonKind && costQuote ? costQuote.cost : Number.isFinite(pricedAddonNominalUnitPrice) ? pricedAddonNominalUnitPrice * count : 0;
    const variantPrice = Number(variant.price || 0);
    const variantOriginalPrice = Number(variant.compare_at_price || 0) > variantPrice ? Number(variant.compare_at_price) : variantPrice;
    const unitPriced = form.dataset.sbUnitPriced === 'true';
    const configuredServicePrice = unitPriced ? variantPrice * count : variantPrice;
    const configuredOriginalServicePrice = unitPriced ? variantOriginalPrice * count : variantOriginalPrice;
    const servicePrice = Math.max(0, configuredServicePrice - formatPrice);
    const originalServicePrice = Math.max(0, configuredOriginalServicePrice - formatPrice);
    const subscriptionSaving = Math.round(servicePrice * subscriptionPercent / 100);
    const discountedServicePrice = Math.max(0, servicePrice - subscriptionSaving);
    const pricedAddonFeePrice = pricedAddonKind === 'booking' ? pricedAddonPrice - pricedAddonNominalPrice : 0;
    const expectedBookingFee = pricedAddonKind === 'booking' ? Math.round(pricedAddonNominalPrice * 0.1) : 0;
    const extraPrice = addonPrice + gigPrice + pricedAddonPrice;
    const totalPrice = discountedServicePrice + formatPrice + extraPrice;
    const volumeSaving = Math.max(0, originalServicePrice - servicePrice);
    const totalSaving = volumeSaving + subscriptionSaving;
    const isFiverrService = costRule?.kind === 'fiverr' || Boolean(gigGroup);
    const money = formatProductMoney;
    const gigPricingValid = !isFiverrService || (
      Boolean(gig)
      && Number.isFinite(gigNominalUnitPrice)
      && gigNominalUnitPrice > 0
      && gigFeeUnitPrice === Math.round(gigNominalUnitPrice * 0.2)
      && gigUnitPrice === gigNominalUnitPrice + gigFeeUnitPrice
    );
    const allocationUnitPrice = Number(sellingPlan?.dataset.sbAllocationPrice || 0);
    const configuredAllocationPrice = unitPriced ? allocationUnitPrice * count : allocationUnitPrice;
    const subscriptionPricingValid = !isSubscription || (
      subscriptionPercent === 10
      && Number.isFinite(configuredAllocationPrice)
      && configuredAllocationPrice === discountedServicePrice + formatPrice
    );
    const linkedPlansValid = !isSubscription || [addon, gig, pricedAddon].filter(Boolean).every((input) => (
      Boolean(input.dataset.sbLinkedSellingPlan)
      && Number.isFinite(Number(input.dataset.sbLinkedAllocationPrice))
      && Number(input.dataset.sbLinkedAllocationPrice) > 0
    ));
    const pricedAddonPricingValid = !pricedAddonGroup || (
      Boolean(pricedAddon)
      && Number.isFinite(pricedAddonNominalUnitPrice)
      && pricedAddonNominalUnitPrice > 0
      && (pricedAddonKind === 'booking'
        ? pricedAddonFeePrice === expectedBookingFee && pricedAddonPrice === pricedAddonNominalPrice + expectedBookingFee
        : pricedAddonUnitPrice === pricedAddonNominalUnitPrice)
    );
    const pricingValues = [addonPrice, formatPrice, gigPrice, pricedAddonPrice, pricedAddonNominalPrice, pricedAddonFeePrice, variantPrice, servicePrice, originalServicePrice, subscriptionSaving, totalPrice];
    const pricingValid = (!customCostInput || Boolean(costQuote)) && (crossStoreEnabled ? (!gigGroup || Boolean(gig)) && (!pricedAddonGroup || Boolean(pricedAddon)) : gigPricingValid && subscriptionPricingValid && linkedPlansValid && pricedAddonPricingValid) && pricingValues.every((value) => Number.isFinite(value) && value >= 0);
    form.dataset.sbPricingValid = pricingValid ? 'true' : 'false';
    const reconciliation = form.querySelector('[data-sb-order-reconciliation]');
    if (reconciliation) reconciliation.hidden = pricingValid;
    form.querySelectorAll('[data-sb-order-total]').forEach((total) => { total.textContent = money(totalPrice, currentCurrency()); });
    form.querySelectorAll('[data-sb-price-per-review]').forEach((unitPrice) => {
      unitPrice.textContent = `${money(totalPrice / count, currentCurrency())} ${ui('per review')}`;
    });
    const summary = form.querySelector('[data-sb-order-summary]');
    if (summary) {
      const configurator = form.dataset.sbConfigurator || form.querySelector('[data-sb-configurator]')?.dataset.sbConfigurator || 'review';
      const packageName = form.querySelector('[data-sb-direct-variant]:checked')?.closest('label')?.querySelector('b')?.textContent?.trim();
      const labels = {
        setup: ui('Profile setup'),
        removal: packageName ? `${packageName} ${ui('package')}` : ui('Removal package'),
        traffic: packageName || ui('Traffic package'),
        likes: `${count} ${ui(count === 1 ? 'helpful vote' : 'helpful votes')}`
      };
      summary.textContent = labels[configurator] || `${ui(customCostInput || gig || pricedAddonKind ? 'Service' : 'Review cost')} ${count} × ${money(originalServicePrice / count, currentCurrency())}`;
    }
    const serviceTotal = form.querySelector('[data-sb-order-service-total]');
    if (serviceTotal) serviceTotal.textContent = money(originalServicePrice, currentCurrency());
    const unit = form.querySelector('[data-sb-order-unit]');
    if (unit) {
      unit.hidden = count <= 1;
      unit.textContent = count > 1 ? `${money(totalPrice / count, currentCurrency())} / ${ui('review')}` : '';
    }
    const savings = form.querySelector('[data-sb-order-savings]');
    if (savings) {
      savings.hidden = totalSaving <= 0;
      savings.textContent = totalSaving > 0 ? `${ui('You save')} ${money(totalSaving, currentCurrency())}` : '';
    }
    const gigRow = form.querySelector('[data-sb-order-gig-row]');
    const gigLabel = form.querySelector('[data-sb-order-gig-label]');
    const gigTotal = form.querySelector('[data-sb-order-gig]');
    if (gigRow) gigRow.hidden = !isFiverrService || gigNominalPrice <= 0;
    if (gigLabel && isFiverrService && gigNominalPrice > 0) gigLabel.textContent = `${ui('Gig cost')} ${count} × ${costSelection?.unitLabel || money(gigNominalUnitPrice, currentCurrency())}`;
    if (gigTotal) gigTotal.textContent = isFiverrService && gigNominalPrice > 0 ? money(gigNominalPrice, currentCurrency()) : '';
    const gigFeeRow = form.querySelector('[data-sb-order-gig-fee-row]');
    const gigFeeTotal = form.querySelector('[data-sb-order-gig-fee]');
    if (gigFeeRow) gigFeeRow.hidden = !isFiverrService || gigFeePrice <= 0;
    if (gigFeeTotal) gigFeeTotal.textContent = isFiverrService && gigFeePrice > 0 ? `+${money(gigFeePrice, currentCurrency())}` : '';
    const volumeRow = form.querySelector('[data-sb-order-volume-row]');
    const volume = form.querySelector('[data-sb-order-volume]');
    const volumeLabel = form.querySelector('[data-sb-order-volume-label]');
    if (volumeRow) volumeRow.hidden = volumeSaving <= 0;
    if (volume) volume.textContent = volumeSaving > 0 ? `−${money(volumeSaving, currentCurrency())}` : '';
    if (volumeLabel && volumeSaving > 0) {
      const volumePercent = originalServicePrice > 0 ? Math.round((volumeSaving / originalServicePrice) * 100) : 0;
      volumeLabel.textContent = `${ui('Volume discount')} (${volumePercent}% × ${count})`;
    }
    const subscriptionRow = form.querySelector('[data-sb-order-subscription-row]');
    const subscription = form.querySelector('[data-sb-order-subscription]');
    const subscriptionLabel = form.querySelector('[data-sb-order-subscription-label]');
    if (subscriptionRow) subscriptionRow.hidden = subscriptionSaving <= 0;
    if (subscription) subscription.textContent = subscriptionSaving > 0 ? `−${money(subscriptionSaving, currentCurrency())}` : '';
    if (subscriptionLabel && subscriptionSaving > 0) subscriptionLabel.textContent = `${ui('Subscription discount')} (${subscriptionPercent}%)`;
    const formatRow = form.querySelector('[data-sb-order-format-row]');
    const formatTotal = form.querySelector('[data-sb-order-format]');
    const formatLabel = form.querySelector('[data-sb-order-format-label]');
    if (formatRow) formatRow.hidden = formatPrice <= 0;
    if (formatTotal) formatTotal.textContent = formatPrice > 0 ? `+${money(formatPrice, currentCurrency())}` : '';
    if (formatLabel && formatPrice > 0) formatLabel.textContent = `${ui('Media format fee')} (${ui(formatChoice?.value || 'Text')})`;
    const genderRow = form.querySelector('[data-sb-order-gender-row]');
    const genderLabel = form.querySelector('[data-sb-order-gender-label]');
    const genderTotal = form.querySelector('[data-sb-order-gender]');
    if (genderRow) genderRow.hidden = addonPrice <= 0;
    if (genderLabel && addonPrice > 0) genderLabel.textContent = ui(`${addon?.value || 'Selected'} reviewer preference`);
    if (genderTotal) genderTotal.textContent = addonPrice > 0 ? `+${money(addonPrice, currentCurrency())}` : '';
    const addonRow = form.querySelector('[data-sb-order-addon-row]');
    const addonLabel = form.querySelector('[data-sb-order-addon-label]');
    const addonTotal = form.querySelector('[data-sb-order-addon]');
    if (addonRow) addonRow.hidden = pricedAddonNominalPrice <= 0;
    if (addonLabel && pricedAddonNominalPrice > 0) {
      const addonLabels = { amazon: 'Product cost', airbnb: 'Reservation cost', booking: 'Booking cost' };
      addonLabel.textContent = `${ui(addonLabels[pricedAddonKind] || 'Additional service')} ${count} × ${costSelection?.unitLabel || money(pricedAddonNominalUnitPrice, currentCurrency())}`;
    }
    if (addonTotal) addonTotal.textContent = pricedAddonNominalPrice > 0 ? money(pricedAddonNominalPrice, currentCurrency()) : '';
    const bookingFeeRow = form.querySelector('[data-sb-order-booking-fee-row]');
    const bookingFeeTotal = form.querySelector('[data-sb-order-booking-fee]');
    if (bookingFeeRow) bookingFeeRow.hidden = pricedAddonKind !== 'booking' || pricedAddonFeePrice <= 0;
    if (bookingFeeTotal) bookingFeeTotal.textContent = pricedAddonKind === 'booking' && pricedAddonFeePrice > 0 ? `+${money(pricedAddonFeePrice, currentCurrency())}` : '';
  };

  const productSellingPlanMap = (form) => {
    if (form._starsBoosterSellingPlanMap) return form._starsBoosterSellingPlanMap;
    try {
      form._starsBoosterSellingPlanMap = JSON.parse(form.querySelector('[data-sb-selling-plan-map]')?.textContent || '{}');
    } catch {
      form._starsBoosterSellingPlanMap = {};
    }
    return form._starsBoosterSellingPlanMap;
  };

  const syncProductSellingPlan = (form, variant) => {
    const subscribe = form.querySelector('[data-sb-subscription-choice]');
    if (!subscriptionsEnabled) {
      if (subscribe) { subscribe.checked = false; subscribe.disabled = true; }
      const oneTime = form.querySelector('input[name="selling_plan"][value=""]');
      if (oneTime) oneTime.checked = true;
      return;
    }
    if (!subscribe || !variant) return;
    const allocation = productSellingPlanMap(form)[String(variant.id)] || null;
    subscribe.value = allocation?.planId ? String(allocation.planId) : '';
    subscribe.dataset.sbAllocationPrice = allocation?.price != null ? String(allocation.price) : '';
    subscribe.disabled = !allocation?.planId;
    if (subscribe.disabled && subscribe.checked) {
      const oneTime = form.querySelector('input[name="selling_plan"][value=""]');
      if (oneTime) oneTime.checked = true;
    }
  };

  const applyProductVariant = (form, variant) => {
    const idInput = form.querySelector('[data-sb-variant-id]');
    if (!idInput || !variant) return;
    const servicePaused = form.dataset.sbServicePaused === 'true';
    idInput.value = String(variant.id);
    if (crossStoreEnabled) {
      const variants = JSON.parse(form.querySelector('[data-sb-product-variants]')?.textContent || '[]');
      const groups = [...form.querySelectorAll('[data-sb-product-option]')];
      groups.forEach((group, index) => group.querySelectorAll('input').forEach(input => {
        const candidates = variants.filter(v => v.options?.[index] === input.value && (index === 0 || v.options?.[0] === groups[0]?.querySelector('input:checked')?.value));
        const unavailable = form.dataset.sbBReady !== 'true' || !candidates.some(v => window.SBStoreB?.quote(v.id).available);
        input.disabled = unavailable;
        input.closest('label')?.setAttribute('title', unavailable ? ui('Unavailable') : '');
      }));
    }
    syncProductSellingPlan(form, variant);
    updateProductPricing(form, variant);
    form.querySelectorAll('[data-sb-product-submit]').forEach((submit) => {
      const stockBlocked = crossStoreEnabled && (form.dataset.sbBReady !== 'true' || !window.SBStoreB?.quote(variant.id).available || [...form.querySelectorAll('[data-sb-linked-addon]:checked, [data-sb-gig-variant]:checked, [data-sb-priced-addon]:checked')].some(input => input.dataset.sbUnavailable === 'true') || (form.querySelector('[data-sb-priced-addon-group]') && !form.querySelector('[data-sb-priced-addon]:checked:not(:disabled)')) || (form.querySelector('[data-sb-gig-selector]') && !form.querySelector('[data-sb-gig-variant]:checked:not(:disabled)')));
      submit.disabled = servicePaused || stockBlocked || (!crossStoreEnabled && !variant.available) || submit.dataset.configUnavailable === 'true' || form.dataset.sbPricingValid === 'false';
      if (crossStoreEnabled) {
        submit.textContent = servicePaused ? ui('Sold out') : form.dataset.sbBReady !== 'true' ? (crossStoreMessages.checking || 'Checking availability…') : stockBlocked ? ui('Unavailable') : submit.hasAttribute('data-sb-buy-now') ? ui('Buy Now') : ui('Add to Cart');
        if (stockBlocked && !servicePaused) form.querySelectorAll('[data-sb-order-total], [data-sb-price-per-review]').forEach(target => { target.textContent = form.dataset.sbBReady === 'true' ? ui('Unavailable') : '—'; });
      }
    });
    const nextUrl = new URL(window.location.href);
    if (!subscriptionsEnabled) nextUrl.searchParams.delete('selling_plan');
    nextUrl.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', nextUrl);
  };

  const syncPricedAddonQuantity = (form) => {
    const group = form.querySelector('[data-sb-priced-addon-mode="bundle"]');
    if (!group) return;
    const quantity = String(selectedReviewCount(form));
    const candidates = [...group.querySelectorAll('[data-sb-addon-quantity]')];
    candidates.forEach((input) => {
      const matches = input.dataset.sbAddonQuantity === quantity;
      input.closest('label').hidden = !matches;
      input.disabled = !matches || input.dataset.sbUnavailable === 'true';
    });
    const visible = candidates.filter((input) => !input.disabled);
    const keepUnavailableSelection = crossStoreEnabled && candidates.some(input => input.checked && input.dataset.sbAddonQuantity === quantity);
    if (!keepUnavailableSelection && !visible.some((input) => input.checked) && visible[0]) visible[0].checked = true;
  };

  const updateProductVariant = (form) => {
    syncPricedAddonQuantity(form);
    const variant = selectedProductVariant(form);
    applyProductVariant(form, variant);
  };

  const setProductMessage = (form, message, error = false) => {
    const target = form.querySelector('[data-sb-product-message]');
    if (!target) return;
    const cleanMessage = String(message || '').trim();
    target.textContent = cleanMessage;
    target.hidden = !cleanMessage;
    target.classList.toggle('is-error', Boolean(cleanMessage && error));
    target.setAttribute('role', error ? 'alert' : 'status');
    if (error && cleanMessage) {
      target.tabIndex = -1;
      target.focus();
    } else {
      target.removeAttribute('tabindex');
    }
  };

  const isValidProfileUrl = (value) => {
    try {
      const url = new URL(String(value || '').trim());
      return url.protocol === 'https:' || url.protocol === 'http:';
    } catch {
      return false;
    }
  };

  const contentMediaRequired = (entry) => entry.closest('[data-sb-content-modal]')?.dataset.sbContentFormat !== 'Text';

  const contentEntryIsReady = (entry) => {
    if (entry.querySelector('[data-sb-stars-only]')?.checked) return true;
    const hasText = Boolean(entry.querySelector('[data-sb-content-text]')?.value.trim());
    const hasMedia = Boolean(entry.querySelector('[data-sb-content-media-input]')?.files?.length);
    return hasText && (!contentMediaRequired(entry) || hasMedia);
  };

  const updateContentEntry = (entry) => {
    const starsOnly = entry.querySelector('[data-sb-stars-only]')?.checked;
    const textInput = entry.querySelector('[data-sb-content-text]');
    const preview = entry.querySelector('[data-sb-content-preview]');
    const charCount = entry.querySelector('[data-sb-content-char-count]');
    const mediaInput = entry.querySelector('[data-sb-content-media-input]');
    const mediaName = entry.querySelector('[data-sb-content-media-name]');
    const mediaAction = entry.querySelector('[data-sb-content-media-action]');
    const mediaRemove = entry.querySelector('[data-sb-content-media-remove]');
    const selectedMedia = mediaInput?.files?.[0];
    const format = entry.closest('[data-sb-content-modal]')?.dataset.sbContentFormat || 'Text';
    entry.classList.toggle('is-stars-only', Boolean(starsOnly));
    if (mediaName) {
      mediaName.hidden = !selectedMedia;
      mediaName.textContent = selectedMedia ? `Attached: ${selectedMedia.name}` : '';
    }
    if (mediaAction) mediaAction.textContent = selectedMedia ? 'Replace' : 'Attach';
    if (mediaRemove) mediaRemove.hidden = !selectedMedia;
    if (!preview) return;
    const textValue = textInput?.value.trim() || '';
    if (charCount) charCount.textContent = `${textValue.replace(/\s+/g, '').length} chars`;
    if (starsOnly) {
      preview.textContent = 'Stars only';
      preview.classList.remove('is-missing');
    } else if (!textValue) {
      preview.textContent = ui('Missing');
      preview.classList.add('is-missing');
    } else if (contentMediaRequired(entry) && !selectedMedia) {
      preview.textContent = format === 'Text + Video' ? 'Needs video' : 'Needs image';
      preview.classList.add('is-missing');
    } else {
      preview.textContent = `${textValue.slice(0, 76)}${textValue.length > 76 ? '…' : ''}`;
      preview.classList.remove('is-missing');
    }
  };

  const updateContentPagination = (modal) => {
    const entries = [...modal.querySelectorAll('[data-sb-content-entry]')];
    const search = modal.querySelector('[data-sb-content-search]')?.value.trim().toLowerCase() || '';
    const filter = modal.querySelector('[data-sb-content-filter]')?.value || 'All';
    const filtered = entries.filter((entry) => {
      const ready = contentEntryIsReady(entry);
      const filterMatches = filter === 'All' || (filter === 'Complete' ? ready : !ready);
      const text = `${entry.querySelector('[data-sb-content-entry-label]')?.textContent || ''} ${entry.querySelector('[data-sb-content-preview]')?.textContent || ''}`.toLowerCase();
      return filterMatches && (!search || text.includes(search));
    });
    const pageSize = entries.length >= 20 ? 10 : Math.max(1, entries.length);
    const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
    const requestedPage = Number.parseInt(modal.dataset.sbContentPage || '0', 10) || 0;
    const page = Math.min(Math.max(0, requestedPage), pageCount - 1);
    modal.dataset.sbContentPage = String(page);
    const start = page * pageSize;
    const visible = filtered.slice(start, start + pageSize);
    entries.forEach((entry) => { entry.hidden = !visible.includes(entry); });
    const count = modal.querySelector('[data-sb-content-page-count]');
    if (count) count.textContent = filtered.length ? `${start + 1}–${Math.min(filtered.length, start + pageSize)} of ${filtered.length}` : '0 items';
    const previous = modal.querySelector('[data-sb-content-page-prev]');
    const next = modal.querySelector('[data-sb-content-page-next]');
    if (previous) previous.disabled = page === 0;
    if (next) next.disabled = page >= pageCount - 1;
    const expandedVisible = visible.some((entry) => entry.querySelector('[data-sb-content-entry-toggle]')?.getAttribute('aria-expanded') === 'true');
    if (!expandedVisible && visible[0]) setContentEntryExpanded(modal, visible[0]);
  };

  const updateContentModal = (modal) => {
    const entries = [...modal.querySelectorAll('[data-sb-content-entry]')];
    entries.forEach(updateContentEntry);
    const ready = entries.filter(contentEntryIsReady).length;
    const name = modal.querySelector('[data-sb-content-business]')?.value.trim() || '';
    const url = modal.querySelector('[data-sb-content-url]')?.value.trim() || '';
    const urlValid = isValidProfileUrl(url);
    const readyCount = modal.querySelector('[data-sb-content-ready-count]');
    const save = modal.querySelector('[data-sb-content-save]');
    const guide = modal.querySelector('[data-sb-content-guide]');
    if (readyCount) readyCount.textContent = `${ready}/${entries.length} ready`;
    if (save) save.disabled = !name || !urlValid || ready !== entries.length;
    if (guide) {
      guide.classList.toggle('is-error', !name || !url || !urlValid);
      guide.textContent = !name || !url
        ? 'Enter your business name and URL to continue.'
        : !urlValid
          ? 'URL format is invalid.'
          : ready === entries.length
            ? 'All content items are complete.'
            : 'Open a content item and click Generate, or use "Generate All Missing" above.';
    }
    updateContentPagination(modal);
  };

  const setContentEntryExpanded = (modal, target) => {
    modal.querySelectorAll('[data-sb-content-entry]').forEach((entry) => {
      const expanded = entry === target;
      entry.querySelector('[data-sb-content-entry-toggle]')?.setAttribute('aria-expanded', String(expanded));
      const editor = entry.querySelector('[data-sb-content-entry-editor]');
      if (editor) editor.hidden = !expanded;
    });
  };

  const populateContentModal = (form, modal) => {
    const list = modal.querySelector('[data-sb-content-list]');
    const template = modal.querySelector('[data-sb-content-template]');
    const count = Math.max(1, selectedReviewCount(form));
    const savedInput = form.querySelector('[data-sb-content-value]');
    const formatChoice = form.querySelector('[data-sb-option-name="Feedback format"] input:checked');
    const format = formatChoice?.value || 'Text';
    const mediaRequired = format !== 'Text';
    const mediaKind = format === 'Text + Video' ? 'Video' : 'Image';
    let saved = null;
    try { saved = savedInput?.value ? JSON.parse(savedInput.value) : null; } catch { saved = null; }
    if (!list || !template) return;
    modal.dataset.sbContentFormat = format;
    list.replaceChildren();
    for (let index = 0; index < count; index += 1) {
      const fragment = template.content.cloneNode(true);
      const entry = fragment.querySelector('[data-sb-content-entry]');
      const label = fragment.querySelector('[data-sb-content-entry-label]');
      if (!entry) continue;
      entry.dataset.sbContentIndex = String(index);
      if (label) label.textContent = `Content ${String(index + 1).padStart(2, '0')}`;
      fragment.querySelectorAll('[data-sb-content-rating]').forEach((input) => { input.name = `sb-content-rating-${index}`; });
      fragment.querySelectorAll('[data-sb-content-gender]').forEach((input) => { input.name = `sb-content-gender-${index}`; });
      const media = fragment.querySelector('[data-sb-content-media]');
      const mediaInput = fragment.querySelector('[data-sb-content-media-input]');
      const mediaKindLabel = fragment.querySelector('[data-sb-content-media-kind]');
      if (media) media.hidden = !mediaRequired;
      if (mediaInput) {
        mediaInput.disabled = !mediaRequired;
        mediaInput.accept = format === 'Text + Video' ? 'video/*' : 'image/*';
        mediaInput.name = `properties[Content ${String(index + 1).padStart(2, '0')} ${mediaKind}]`;
      }
      if (mediaKindLabel) mediaKindLabel.textContent = mediaKind;
      const savedEntry = saved?.entries?.[index];
      if (savedEntry) {
        const rating = fragment.querySelector(`[data-sb-content-rating][value="${Number(savedEntry.rating || 5)}"]`);
        if (rating) rating.checked = true;
        const gender = fragment.querySelector(`[data-sb-content-gender][value="${CSS.escape(savedEntry.gender || 'Any')}"]`);
        if (gender) gender.checked = true;
        const stars = fragment.querySelector('[data-sb-stars-only]');
        if (stars) stars.checked = Boolean(savedEntry.starsOnly);
        const textInput = fragment.querySelector('[data-sb-content-text]');
        if (textInput) textInput.value = savedEntry.text || '';
      }
      const editor = fragment.querySelector('[data-sb-content-entry-editor]');
      const toggle = fragment.querySelector('[data-sb-content-entry-toggle]');
      const previous = fragment.querySelector('[data-sb-content-entry-prev]');
      const next = fragment.querySelector('[data-sb-content-entry-next]');
      if (editor) editor.hidden = index !== 0;
      if (toggle) toggle.setAttribute('aria-expanded', String(index === 0));
      if (previous) previous.disabled = index === 0;
      if (next) next.disabled = index === count - 1;
      list.append(fragment);
    }
    const business = modal.querySelector('[data-sb-content-business]');
    const profileUrl = modal.querySelector('[data-sb-content-url]');
    if (business) business.value = saved?.businessName || form.querySelector('[data-sb-content-business-value]')?.value || '';
    if (profileUrl) profileUrl.value = form.querySelector('#StarsBoosterBusinessUrl')?.value || saved?.businessUrl || '';
    const itemsChip = modal.querySelector('[data-sb-content-items]');
    const formatChip = modal.querySelector('[data-sb-content-format]');
    const toolbar = modal.querySelector('[data-sb-content-toolbar]');
    if (itemsChip) itemsChip.textContent = `${count} items`;
    if (formatChip) formatChip.textContent = format;
    if (toolbar) toolbar.hidden = count <= 1;
    modal.dataset.sbContentPage = '0';
    const search = modal.querySelector('[data-sb-content-search]');
    const filter = modal.querySelector('[data-sb-content-filter]');
    if (search) search.value = '';
    if (filter) filter.value = 'All';
    modal.dataset.sbContentDirty = 'false';
    updateContentModal(modal);
  };

  const openContentModal = (form, trigger) => {
    const modal = form.querySelector('[data-sb-content-modal]');
    if (!modal) return;
    contentModalReturnFocus = trigger || document.activeElement;
    populateContentModal(form, modal);
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('sb-modal-open');
    modal.querySelector('[data-sb-content-close]')?.focus();
  };

  const closeContentModal = (modal, discard = false) => {
    if (!modal) return;
    const closeConfirm = modal.querySelector('[data-sb-content-close-confirm]');
    if (!discard && modal.dataset.sbContentDirty === 'true' && closeConfirm) {
      closeConfirm.hidden = false;
      closeConfirm.querySelector('[data-sb-content-continue]')?.focus();
      return;
    }
    if (closeConfirm) closeConfirm.hidden = true;
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('sb-modal-open');
    contentModalReturnFocus?.focus?.();
  };

  const serializedContent = (modal) => ({
    businessName: modal.querySelector('[data-sb-content-business]')?.value.trim() || '',
    businessUrl: modal.querySelector('[data-sb-content-url]')?.value.trim() || '',
    entries: [...modal.querySelectorAll('[data-sb-content-entry]')].map((entry) => ({
      rating: Number(entry.querySelector('[data-sb-content-rating]:checked')?.value || 5),
      gender: entry.querySelector('[data-sb-content-gender]:checked')?.value || 'Any',
      starsOnly: Boolean(entry.querySelector('[data-sb-stars-only]')?.checked),
      text: entry.querySelector('[data-sb-content-text]')?.value.trim() || '',
      media: entry.querySelector('[data-sb-content-media-input]')?.files?.[0]?.name || ''
    }))
  });

  const formProperties = (formData) => {
    const properties = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)]$/);
      if (match && String(value).trim()) properties[match[1]] = String(value);
    }
    return properties;
  };

  const fetchCartState = async () => {
    const response = await cartRequest(localeRoute('/cart.js'), { cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart is temporarily unavailable. Please refresh and try again.');
    return response.json();
  };

  const updateCartItems = async (updates) => {
    const response = await cartRequest(localeRoute('/cart/update.js'), {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });
    if (!response.ok) throw new Error('The cart could not be repaired safely. Please refresh or contact support.');
    return response.json();
  };

  const cleanupOrphanCartItems = async (cart) => {
    const orphans = analyzeCart(cart).orphanItems;
    if (!orphans.length) return cart;
    await updateCartItems(Object.fromEntries(orphans.map((item) => [item.key, 0])));
    const repaired = await fetchCartState();
    if (analyzeCart(repaired).orphanItems.length) throw new Error('The cart still contains incomplete service selections. Checkout remains blocked; please refresh or contact support.');
    return repaired;
  };

  const rollbackCartBundle = async (bundle) => {
    try {
      const cart = await fetchCartState();
      const bundleItems = cart.items.filter((item) => cartItemBundleId(item) === bundle);
      if (!bundleItems.length) return true;
      await updateCartItems(Object.fromEntries(bundleItems.map((item) => [item.key, 0])));
      const verified = await fetchCartState();
      return !verified.items.some((item) => cartItemBundleId(item) === bundle);
    } catch (_) {
      return false;
    }
  };

  const addProductToCart = async (form) => {
    enforceCustomerMedia(form, selectedProductVariant(form));
    if (form.dataset.sbPricingValid === 'false') throw new Error(form.querySelector('[data-sb-custom-cost-error]:not([hidden])')?.textContent || 'This configuration cannot be reconciled with the current Shopify prices.');
    const data = new FormData(form);
    if (!subscriptionsEnabled && data.get('selling_plan')) throw new Error(subscriptionNotice());
    const gigGroup = form.querySelector('[data-sb-gig-selector]');
    const gig = form.querySelector('[data-sb-gig-variant]:checked:not(:disabled)');
    const linkedAddon = form.querySelector('[data-sb-linked-addon]:checked:not(:disabled)');
    const pricedAddonGroup = form.querySelector('[data-sb-priced-addon-group]');
    const pricedAddon = form.querySelector('[data-sb-priced-addon]:checked:not(:disabled)');
    const hasContentMedia = [...form.querySelectorAll('[data-sb-content-media-input]')].some((input) => input.files?.length);
    if (hasContentMedia) throw new Error('This media attachment cannot be added with the selected priced add-on. Choose Text or contact support.');
    if (gigGroup && !gig) throw new Error('Choose a prepaid gig cost before adding this service.');
    if (pricedAddonGroup && !pricedAddon) throw new Error('Choose the required price tier before adding this service.');
    let cart = await fetchCartState();
    cart = await cleanupOrphanCartItems(cart);
    const bundle = window.crypto?.randomUUID?.() || `sb-${Date.now()}`;
    const properties = formProperties(data);
    properties['_StarsBooster bundle'] = bundle;
    const customCostInput = form.querySelector('[data-sb-custom-cost]');
    if (customCostInput) {
      const costRule = window.SBCheckoutSourceRules?.[String(data.get('id'))]?.costRule;
      if (!costRule || !window.SBCostInput) throw Error('The cost calculator is loading. Please try again.');
      Object.assign(properties, window.SBCostInput.read(customCostInput, costRule.units, costRule.feeBasisPoints).properties);
    }
    const serviceItem = { id: Number(data.get('id')), quantity: Number(data.get('quantity') || 1), properties };
    const sellingPlan = data.get('selling_plan');
    if (sellingPlan) serviceItem.selling_plan = Number(sellingPlan);
    const recurringLine = (item, input) => {
      if (!sellingPlan) return item;
      const linkedSellingPlan = Number(input?.dataset.sbLinkedSellingPlan || 0);
      if (!linkedSellingPlan) throw new Error('The selected linked service is not available for monthly renewal. Refresh or contact support.');
      return { ...item, selling_plan: linkedSellingPlan };
    };
    const linkedItems = [];
    if (gig) linkedItems.push(recurringLine({ id: Number(gig.value), quantity: Math.max(1, selectedReviewCount(form)), properties: { '_StarsBooster bundle': bundle, 'Gig cost': gig.dataset.sbGigLabel || gig.value } }, gig));
    if (linkedAddon) linkedItems.push(recurringLine({ id: Number(linkedAddon.dataset.sbLinkedAddon), quantity: Math.max(1, selectedReviewCount(form)), properties: { '_StarsBooster bundle': bundle, 'Add-on': linkedAddon.value } }, linkedAddon));
    if (pricedAddon) linkedItems.push(recurringLine({ id: Number(pricedAddon.dataset.sbPricedAddon), quantity: pricedAddon.dataset.sbAddonQuantity ? 1 : Math.max(1, selectedReviewCount(form)), properties: { '_StarsBooster bundle': bundle, 'Priced add-on': pricedAddon.value } }, pricedAddon));
    let response;
    try {
      if (!linkedItems.length && hasContentMedia) {
        data.set('properties[_StarsBooster bundle]', bundle);
        response = await cartRequest(localeRoute('/cart/add.js'), { method: 'POST', headers: { Accept: 'application/json' }, body: data });
      } else {
        response = await cartRequest(localeRoute('/cart/add.js'), {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [serviceItem, ...linkedItems] })
        });
      }
    } catch (_) {
      const rolledBack = await rollbackCartBundle(bundle);
      throw new Error(rolledBack ? 'We could not add a complete order. No partial items were kept. Please try again.' : 'We could not safely complete this order. Checkout is blocked until the cart is refreshed or support is contacted.');
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const rolledBack = await rollbackCartBundle(bundle);
      if (!rolledBack) throw new Error('We could not safely complete this order. Checkout is blocked until the cart is refreshed or support is contacted.');
      throw new Error(body.description || 'We could not add a complete order. No partial items were kept. Please try again.');
    }
    const addedCart = await fetchCartState();
    const created = addedCart.items.filter((item) => cartItemBundleId(item) === bundle);
    const primaryCount = created.filter((item) => !cartItemIsAddon(item)).length;
    const addonCount = created.filter(cartItemIsAddon).length;
    if (primaryCount !== 1 || addonCount !== linkedItems.length) {
      const rolledBack = await rollbackCartBundle(bundle);
      throw new Error(rolledBack ? 'We could not add a complete order. No partial items were kept. Please try again.' : 'We could not safely complete this order. Checkout is blocked until the cart is refreshed or support is contacted.');
    }
    window.__starsBoosterCart = addedCart;
    return addedCart;
  };

  document.addEventListener('change', (event) => {
    const localization = event.target.closest('[data-sb-localization]');
    if (localization) { localization.form?.submit(); return; }
    const directVariant = event.target.closest('[data-sb-direct-variant]');
    if (directVariant) {
      const form = directVariant.closest('form');
      const source = form?.querySelector('[data-sb-product-variants]');
      let variants = [];
      try { variants = JSON.parse(source?.textContent || '[]'); } catch { variants = []; }
      const variant = variants.find((item) => String(item.id) === directVariant.value);
      if (form && variant) applyProductVariant(form, variant);
      return;
    }
    const serviceArea = event.target.closest('[data-sb-service-area-toggle]');
    if (serviceArea) {
      const form = serviceArea.closest('form');
      const physical = form?.querySelector('[data-sb-physical-address]');
      const areas = form?.querySelector('[data-sb-service-areas]');
      if (physical) {
        physical.hidden = serviceArea.checked;
        physical.querySelectorAll('input').forEach((input) => { input.disabled = serviceArea.checked; });
      }
      if (areas) {
        areas.hidden = !serviceArea.checked;
        areas.querySelectorAll('input').forEach((input) => { input.disabled = !serviceArea.checked; });
      }
      return;
    }
    const contentChoice = event.target.closest('[data-sb-content-choice]');
    if (contentChoice) {
      const form = contentChoice.closest('form');
      const providing = contentChoice.dataset.sbContentChoice === 'provide';
      const instructions = form?.querySelector('[data-sb-writing-instructions]');
      if (instructions) { instructions.hidden = providing; instructions.querySelectorAll('textarea').forEach(input => { input.disabled = providing; }); }
      const modalBusinessValue = form?.querySelector('[data-sb-content-business-value]');
      if (modalBusinessValue) modalBusinessValue.disabled = !providing;
      if (!providing) {
        const contentValue = form?.querySelector('[data-sb-content-value]');
        if (contentValue) contentValue.value = '';
      }
      updateProductPricing(form);
      return;
    }

    const contentField = event.target.closest('[data-sb-content-modal] input, [data-sb-content-modal] textarea, [data-sb-content-modal] select');
    if (contentField) {
      const modal = contentField.closest('[data-sb-content-modal]');
      modal.dataset.sbContentDirty = 'true';
      updateContentModal(modal);
      return;
    }
    if (event.target.closest('[data-sb-product-option]')) updateProductVariant(event.target.closest('form'));
    if (event.target.closest('[data-sb-linked-addon]') || event.target.closest('[data-sb-priced-addon]') || event.target.name === 'properties[Reviewer gender]' || event.target.name === 'selling_plan') updateProductPricing(event.target.closest('form'));
    const gig = event.target.closest('[data-sb-gig-variant]');
    if (gig) updateProductPricing(gig.closest('form'));
  });

  document.addEventListener('input', (event) => {
    const customCost = event.target.closest('[data-sb-custom-cost]');
    if (customCost) { updateProductVariant(customCost.closest('form')); return; }
    const field = event.target.closest('[data-sb-content-modal] input, [data-sb-content-modal] textarea, [data-sb-content-modal] select');
    if (!field) return;
    const modal = field.closest('[data-sb-content-modal]');
    modal.dataset.sbContentDirty = 'true';
    updateContentModal(modal);
  });

  document.addEventListener('click', async (event) => {
    document.querySelectorAll('.sb-service-picker details[open]').forEach((picker) => {
      if (!picker.contains(event.target)) picker.removeAttribute('open');
    });
    document.querySelectorAll('[data-sb-products-menu][open]').forEach((menu) => {
      if (!menu.contains(event.target)) menu.removeAttribute('open');
    });

    const contentPagePrevious = event.target.closest('[data-sb-content-page-prev]');
    if (contentPagePrevious) {
      const modal = contentPagePrevious.closest('[data-sb-content-modal]');
      modal.dataset.sbContentPage = String(Math.max(0, (Number.parseInt(modal.dataset.sbContentPage || '0', 10) || 0) - 1));
      updateContentPagination(modal);
      return;
    }

    const contentPageNext = event.target.closest('[data-sb-content-page-next]');
    if (contentPageNext) {
      const modal = contentPageNext.closest('[data-sb-content-modal]');
      modal.dataset.sbContentPage = String((Number.parseInt(modal.dataset.sbContentPage || '0', 10) || 0) + 1);
      updateContentPagination(modal);
      return;
    }

    const contentEntryStep = event.target.closest('[data-sb-content-entry-prev], [data-sb-content-entry-next]');
    if (contentEntryStep) {
      const modal = contentEntryStep.closest('[data-sb-content-modal]');
      const entries = [...modal.querySelectorAll('[data-sb-content-entry]')];
      const current = contentEntryStep.closest('[data-sb-content-entry]');
      const currentIndex = entries.indexOf(current);
      const nextIndex = Math.min(entries.length - 1, Math.max(0, currentIndex + (contentEntryStep.matches('[data-sb-content-entry-next]') ? 1 : -1)));
      const pageSize = entries.length >= 20 ? 10 : Math.max(1, entries.length);
      modal.dataset.sbContentPage = String(Math.floor(nextIndex / pageSize));
      updateContentPagination(modal);
      setContentEntryExpanded(modal, entries[nextIndex]);
      entries[nextIndex]?.querySelector('[data-sb-content-entry-toggle]')?.focus();
      return;
    }

    const contentClose = event.target.closest('[data-sb-content-close]');
    if (contentClose) {
      closeContentModal(contentClose.closest('[data-sb-content-modal]'));
      return;
    }

    const contentContinue = event.target.closest('[data-sb-content-continue]');
    if (contentContinue) {
      const confirm = contentContinue.closest('[data-sb-content-close-confirm]');
      if (confirm) confirm.hidden = true;
      contentContinue.closest('[data-sb-content-modal]')?.querySelector('[data-sb-content-close]')?.focus();
      return;
    }

    const contentDiscard = event.target.closest('[data-sb-content-discard]');
    if (contentDiscard) {
      closeContentModal(contentDiscard.closest('[data-sb-content-modal]'), true);
      return;
    }

    const contentEntryToggle = event.target.closest('[data-sb-content-entry-toggle]');
    if (contentEntryToggle) {
      const modal = contentEntryToggle.closest('[data-sb-content-modal]');
      const entry = contentEntryToggle.closest('[data-sb-content-entry]');
      const alreadyOpen = contentEntryToggle.getAttribute('aria-expanded') === 'true';
      if (alreadyOpen) {
        contentEntryToggle.setAttribute('aria-expanded', 'false');
        const editor = entry.querySelector('[data-sb-content-entry-editor]');
        if (editor) editor.hidden = true;
      } else {
        setContentEntryExpanded(modal, entry);
      }
      return;
    }

    const contentDuplicate = event.target.closest('[data-sb-content-duplicate]');
    if (contentDuplicate) {
      const modal = contentDuplicate.closest('[data-sb-content-modal]');
      const entries = [...modal.querySelectorAll('[data-sb-content-entry]')];
      const current = contentDuplicate.closest('[data-sb-content-entry]');
      const index = entries.indexOf(current);
      const next = entries[index + 1];
      if (next) {
        const currentText = current.querySelector('[data-sb-content-text]')?.value || '';
        const nextText = next.querySelector('[data-sb-content-text]');
        if (nextText) nextText.value = currentText;
        const rating = current.querySelector('[data-sb-content-rating]:checked')?.value;
        const nextRating = next.querySelector(`[data-sb-content-rating][value="${rating}"]`);
        if (nextRating) nextRating.checked = true;
        const gender = current.querySelector('[data-sb-content-gender]:checked')?.value;
        const nextGender = next.querySelector(`[data-sb-content-gender][value="${CSS.escape(gender || 'Any')}"]`);
        if (nextGender) nextGender.checked = true;
        const nextStars = next.querySelector('[data-sb-stars-only]');
        if (nextStars) nextStars.checked = Boolean(current.querySelector('[data-sb-stars-only]')?.checked);
        modal.dataset.sbContentDirty = 'true';
        const pageSize = entries.length >= 20 ? 10 : Math.max(1, entries.length);
        modal.dataset.sbContentPage = String(Math.floor((index + 1) / pageSize));
        setContentEntryExpanded(modal, next);
        updateContentModal(modal);
      }
      return;
    }

    const contentClear = event.target.closest('[data-sb-content-clear]');
    if (contentClear) {
      const modal = contentClear.closest('[data-sb-content-modal]');
      const entry = contentClear.closest('[data-sb-content-entry]');
      const textInput = entry.querySelector('[data-sb-content-text]');
      const starsOnly = entry.querySelector('[data-sb-stars-only]');
      if (textInput) textInput.value = '';
      if (starsOnly) starsOnly.checked = false;
      const mediaInput = entry.querySelector('[data-sb-content-media-input]');
      if (mediaInput) mediaInput.value = '';
      modal.dataset.sbContentDirty = 'true';
      updateContentModal(modal);
      return;
    }

    const contentMediaRemove = event.target.closest('[data-sb-content-media-remove]');
    if (contentMediaRemove) {
      const modal = contentMediaRemove.closest('[data-sb-content-modal]');
      const mediaInput = contentMediaRemove.closest('[data-sb-content-entry]')?.querySelector('[data-sb-content-media-input]');
      if (mediaInput) mediaInput.value = '';
      modal.dataset.sbContentDirty = 'true';
      updateContentModal(modal);
      return;
    }

    const contentSave = event.target.closest('[data-sb-content-save]');
    if (contentSave) {
      const modal = contentSave.closest('[data-sb-content-modal]');
      updateContentModal(modal);
      if (contentSave.disabled) return;
      const confirm = modal.querySelector('[data-sb-content-confirm]');
      if (confirm) {
        confirm.hidden = false;
        confirm.querySelector('[data-sb-content-confirm-cancel]')?.focus();
      }
      return;
    }

    const contentConfirmCancel = event.target.closest('[data-sb-content-confirm-cancel]');
    if (contentConfirmCancel) {
      const confirm = contentConfirmCancel.closest('[data-sb-content-confirm]');
      if (confirm) confirm.hidden = true;
      contentConfirmCancel.closest('[data-sb-content-modal]')?.querySelector('[data-sb-content-save]')?.focus();
      return;
    }

    const contentConfirmSave = event.target.closest('[data-sb-content-confirm-save]');
    if (contentConfirmSave) {
      const modal = contentConfirmSave.closest('[data-sb-content-modal]');
      const form = modal.closest('form');
      const payload = serializedContent(modal);
      const contentValue = form.querySelector('[data-sb-content-value]');
      const businessValue = form.querySelector('[data-sb-content-business-value]');
      const urlValue = form.querySelector('#StarsBoosterBusinessUrl');
      if (contentValue) contentValue.value = JSON.stringify(payload);
      if (businessValue) businessValue.value = payload.businessName;
      if (urlValue) urlValue.value = payload.businessUrl;
      modal.dataset.sbContentDirty = 'false';
      const confirm = modal.querySelector('[data-sb-content-confirm]');
      if (confirm) confirm.hidden = true;
      closeContentModal(modal, true);
      setProductMessage(form, ui('Content attached ✓'));
      return;
    }

    const galleryFilter = event.target.closest('[data-sb-gallery-filter]');
    if (galleryFilter) {
      const gallery = galleryFilter.closest('.sb-gallery-page');
      gallery?.querySelectorAll('[data-sb-gallery-filter]').forEach((button) => button.setAttribute('aria-pressed', String(button === galleryFilter)));
      gallery?.dispatchEvent(new CustomEvent('starsbooster:gallery-filter', { bubbles: true, detail: { filter: galleryFilter.dataset.sbGalleryFilter } }));
      return;
    }

    const removalTab = event.target.closest('[data-sb-removal-tab]');
    if (removalTab) {
      if (removalTab.disabled || (!subscriptionsEnabled && removalTab.dataset.sbRemovalTab === 'monthly')) return;
      const page = removalTab.closest('[data-sb-removal-page]');
      page?.querySelectorAll('[data-sb-removal-tab]').forEach((tab) => tab.setAttribute('aria-selected', String(tab === removalTab)));
      page?.querySelectorAll('[data-sb-removal-panel]').forEach((panel) => { panel.hidden = panel.dataset.sbRemovalPanel !== removalTab.dataset.sbRemovalTab; });
      return;
    }

    const menuButton = event.target.closest('[data-sb-menu-toggle]');
    if (menuButton) {
      const menu = document.querySelector('#StarsBoosterMobileNav');
      if (!menu) return;
      menu.hidden = !menu.hidden;
      menuButton.setAttribute('aria-expanded', String(!menu.hidden));
      return;
    }

    const platformButton = event.target.closest('[data-sb-platform-button]');
    if (platformButton) {
      const picker = platformButton.closest('[data-sb-platform-picker]');
      picker?.querySelectorAll('[data-sb-platform-panel]').forEach((panel) => { panel.hidden = panel.dataset.sbPlatformPanel !== platformButton.dataset.sbPlatformButton; });
      const list = picker?.querySelector('[data-sb-platform-list]');
      if (list) list.hidden = true;
      platformButton.setAttribute('aria-expanded', 'true');
      if (event.detail === 0) picker?.querySelector(`[data-sb-platform-panel="${platformButton.dataset.sbPlatformButton}"] button`)?.focus();
      return;
    }
    const platformBack = event.target.closest('[data-sb-platform-back]');
    if (platformBack) {
      const picker = platformBack.closest('[data-sb-platform-picker]');
      picker?.querySelectorAll('[data-sb-platform-panel]').forEach((panel) => { panel.hidden = true; });
      const list = picker?.querySelector('[data-sb-platform-list]');
      if (list) list.hidden = false;
      const active = picker?.querySelector('[data-sb-platform-button][aria-expanded="true"]');
      active?.setAttribute('aria-expanded', 'false');
      if (event.detail === 0) active?.focus();
      return;
    }

    const cartOpen = event.target.closest('[data-sb-cart-open]');
    if (cartOpen) { await refreshCartDrawer(); openCart(cartOpen); return; }
    if (event.target.closest('[data-sb-cart-close]')) { closeCart(); return; }
    if (event.target.closest('[data-sb-cart-continue]')) { closeCart(); return; }

    const cartExpand = event.target.closest('[data-sb-cart-expand]');
    if (cartExpand) {
      const order = cartExpand.closest('[data-sb-cart-order]');
      const details = order?.querySelector('[data-sb-cart-details]');
      if (!details) return;
      const expanded = details.hidden;
      details.hidden = !expanded;
      order.querySelectorAll('[data-sb-cart-expand]').forEach((control) => control.setAttribute('aria-expanded', String(expanded)));
      return;
    }

    const cartChange = event.target.closest('[data-sb-cart-change]');
    if (cartChange) {
      if (crossStoreEnabled && (checkoutInProgress || pendingCartChanges)) { announce(crossStoreMessages.pending); return; }
      pendingCartChanges += 1;
      cartChange.disabled = true;
      try {
        const response = await updateCartBundle(cartChange);
        if (!response) return;
        if (!response.ok) throw new Error('Unable to update this cart item.');
        notifyCartMutation();
        if (cartChange.closest('[data-sb-cart-page]')) {
          window.location.reload();
          return;
        }
        await refreshCartDrawer();
      } catch (error) { announce(error instanceof Error ? error.message : 'Unable to update this cart item.'); }
      finally { pendingCartChanges -= 1; cartChange.disabled = false; }
      return;
    }

    const quantityStep = event.target.closest('[data-sb-quantity-step]');
    if (quantityStep) {
      const input = quantityStep.parentElement?.querySelector('input[type="number"]');
      if (input) input.value = String(Math.max(Number(input.min || 1), Number(input.value || 1) + Number(quantityStep.dataset.sbQuantityStep)));
      return;
    }

    const quantityPreset = event.target.closest('[data-sb-quantity-value]');
    if (quantityPreset) {
      const group = quantityPreset.closest('.sb-review-quantity');
      const input = group?.querySelector('input[name="quantity"]');
      if (input) input.value = quantityPreset.dataset.sbQuantityValue || '1';
      group?.querySelectorAll('[data-sb-quantity-value]').forEach((button) => button.setAttribute('aria-pressed', String(button === quantityPreset)));
      const form = quantityPreset.closest('form');
      if (form) updateProductVariant(form);
      return;
    }

    const mediaStepper = event.target.closest('[data-sb-media-previous], [data-sb-media-next]');
    if (mediaStepper) {
      const gallery = mediaStepper.closest('[data-sb-product-media-gallery]');
      const buttons = [...(gallery?.querySelectorAll('[data-sb-media-target]') || [])];
      const selectedIndex = buttons.findIndex((button) => button.getAttribute('aria-pressed') === 'true');
      const direction = mediaStepper.hasAttribute('data-sb-media-next') ? 1 : -1;
      const targetIndex = Math.max(0, Math.min(buttons.length - 1, selectedIndex + direction));
      buttons[targetIndex]?.click();
      return;
    }

    const mediaTarget = event.target.closest('[data-sb-media-target]');
    if (mediaTarget) {
      const gallery = mediaTarget.closest('[data-sb-product-media-gallery]');
      const selectedMediaId = mediaTarget.dataset.sbMediaTarget;
      gallery?.querySelectorAll('[data-media-id]').forEach((media) => {
        media.hidden = media.dataset.mediaId !== selectedMediaId;
      });
      gallery?.querySelectorAll('[data-sb-media-target]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button === mediaTarget));
      });
      const buttons = [...(gallery?.querySelectorAll('[data-sb-media-target]') || [])];
      const selectedIndex = buttons.indexOf(mediaTarget);
      const previous = gallery?.querySelector('[data-sb-media-previous]');
      const next = gallery?.querySelector('[data-sb-media-next]');
      if (previous) previous.disabled = selectedIndex <= 0;
      if (next) next.disabled = selectedIndex >= buttons.length - 1;
      mediaTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      return;
    }

    const productSubmit = event.target.closest('[data-sb-product-submit]');
    if (!productSubmit) return;
    const form = productSubmit.closest('form[action*="/cart/add"]');
    if (!form) return;
    event.preventDefault();
    if (!form.reportValidity()) return;
    if (crossStoreEnabled && (checkoutInProgress || pendingCartChanges)) { setProductMessage(form, crossStoreMessages.pending, true); return; }
    let mutationPending = true;
    pendingCartChanges += 1;
    productSubmit.disabled = true;
    productSubmit.setAttribute('aria-busy', 'true');
    setProductMessage(form, 'Adding to cart…');
    try {
      await addProductToCart(form);
      pendingCartChanges -= 1;
      mutationPending = false;
      setProductMessage(form, 'Added to cart.');
      notifyCartMutation();
      announce(`${document.querySelector('.sb-product-panel h1')?.textContent || ui('Service')} ${ui('added to cart.')}`);
      if (productSubmit.matches('[data-sb-buy-now]')) {
        const checkoutCart = await fetchCartState();
        syncSubscriptionCartState(checkoutCart);
        assertCheckoutAvailable(checkoutCart);
        await openCheckout(checkoutCart, productSubmit.dataset.sbCheckoutUrl || '/checkout');
        return;
      }
      await refreshCartDrawer();
      openCart(productSubmit);
    } catch (error) {
      setProductMessage(form, error instanceof Error ? error.message : 'Unable to add this service.', true);
    } finally {
      if (mutationPending) pendingCartChanges -= 1;
      productSubmit.removeAttribute('aria-busy');
      if (crossStoreEnabled) await refreshProductAvailability(true);
      else productSubmit.disabled = false;
    }
  });

  document.addEventListener('submit', async (event) => {
    const productForm = event.target.closest('form[action*="/cart/add"]');
    if (productForm) enforceCustomerMedia(productForm, selectedProductVariant(productForm));
    if (productForm && !subscriptionsEnabled && new FormData(productForm).get('selling_plan')) {
      event.preventDefault();
      setProductMessage(productForm, subscriptionNotice(), true);
      return;
    }
    if (crossStoreEnabled && productForm) {
      event.preventDefault();
      const button = productForm.querySelector('[data-sb-product-submit]');
      if (button && !button.disabled) button.click();
      return;
    }
    const cartForm = event.target.closest('[data-sb-cart-page]');
    if (cartForm) {
      event.preventDefault();
      const checkout = cartForm.querySelector('[name="checkout"]');
      if (checkout?.hasAttribute('aria-busy')) return;
      if (checkout) { checkout.disabled = true; checkout.setAttribute('aria-busy', 'true'); }
      let currentCart;
      try {
        currentCart = await fetchCartState();
        syncSubscriptionCartState(currentCart);
        assertCheckoutAvailable(currentCart);
        await openCheckout(currentCart, localeRoute('/checkout'));
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Cart is temporarily unavailable. Please refresh and try again.';
        const status = cartForm.querySelector('.sb-form-status');
        if (status) { status.textContent = message; status.hidden = false; status.tabIndex = -1; status.focus(); }
        announce(message);
      } finally {
        checkout?.removeAttribute('aria-busy');
        if (currentCart) syncSubscriptionCartState(currentCart);
        else if (checkout) checkout.disabled = cartForm.dataset.sbSubscriptionsBlocked === 'true' || cartForm.dataset.sbCartIntegrity === 'invalid';
      }
      return;
    }
    const invalidCart = event.target.closest('[data-sb-cart-page][data-sb-cart-integrity="invalid"]');
    if (!invalidCart) return;
    event.preventDefault();
    const alert = invalidCart.querySelector('[data-sb-cart-integrity-alert]') || document.querySelector('[data-sb-cart-integrity-alert]');
    if (alert) {
      alert.hidden = false;
      alert.tabIndex = -1;
      alert.focus();
    }
    announce('Checkout is blocked because the cart contains incomplete service selections. Add the affected service again to repair the cart or contact support.');
  });

  document.addEventListener('keydown', (event) => {
    const openContentModalElement = document.querySelector('[data-sb-content-modal][aria-hidden="false"]');
    if (event.key === 'Escape') {
      const openProductsMenus = [...document.querySelectorAll('[data-sb-products-menu][open]')];
      const focusedProductsMenu = openProductsMenus.find((menu) => menu.contains(document.activeElement));
      if (openContentModalElement) closeContentModal(openContentModalElement);
      else closeCart();
      closeMobileMenu();
      document.querySelectorAll('.sb-service-picker details[open]').forEach((picker) => picker.removeAttribute('open'));
      openProductsMenus.forEach((menu) => menu.removeAttribute('open'));
      focusedProductsMenu?.querySelector('summary')?.focus();
    }
    const activeDialog = openContentModalElement || (cartDrawer?.getAttribute('aria-hidden') === 'false' ? cartDrawer : null);
    if (event.key === 'Tab' && activeDialog) {
      const focusable = [...activeDialog.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled])')];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  });

  document.querySelectorAll('[data-sb-predictive-input]').forEach((input) => {
    input.addEventListener('input', async () => {
      const target = document.querySelector('[data-sb-predictive]');
      const query = input.value.trim();
      predictiveController?.abort();
      if (!target || query.length < 2) {
        if (target) { target.hidden = true; target.replaceChildren(); }
        input.setAttribute('aria-expanded', 'false');
        return;
      }
      predictiveController = new AbortController();
      try {
        const url = `/search/suggest?q=${encodeURIComponent(query)}&resources[type]=product,article,page&resources[limit]=10&section_id=predictive-search`;
        const response = await window.fetch(url, { signal: predictiveController.signal, headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Predictive search unavailable');
        target.innerHTML = await response.text();
        target.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      } catch (error) {
        if (error?.name !== 'AbortError') { target.hidden = true; input.setAttribute('aria-expanded', 'false'); }
      }
    });
  });

  document.querySelectorAll('[data-sb-paged-list]').forEach((list) => {
    const items = [...list.querySelectorAll('[data-sb-paged-item]')];
    const pageSize = Math.max(1, Number(list.dataset.sbPageSize || items.length || 1));
    const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
    const parameter = list.dataset.sbPageParam || 'page';
    const controls = list.nextElementSibling?.matches('[data-sb-local-pagination]')
      ? list.nextElementSibling
      : null;
    if (!controls) return;

    const previous = controls.querySelector('[data-sb-page-previous]');
    const next = controls.querySelector('[data-sb-page-next]');
    const status = controls.querySelector('[data-sb-page-status]');
    const requestedPage = Number(new URL(window.location.href).searchParams.get(parameter) || 1);
    let currentPage = Math.min(
      pageCount,
      Math.max(1, Number.isInteger(requestedPage) ? requestedPage : 1),
    );

    const renderPage = (replaceHistory = false) => {
      const firstVisibleIndex = (currentPage - 1) * pageSize;
      const lastVisibleIndex = firstVisibleIndex + pageSize;
      items.forEach((item, index) => {
        item.hidden = index < firstVisibleIndex || index >= lastVisibleIndex;
      });
      if (previous) previous.disabled = currentPage <= 1;
      if (next) next.disabled = currentPage >= pageCount;
      if (status) status.textContent = `${currentPage} / ${pageCount}`;
      controls.hidden = pageCount <= 1;

      if (replaceHistory) {
        const url = new URL(window.location.href);
        if (currentPage === 1) url.searchParams.delete(parameter);
        else url.searchParams.set(parameter, String(currentPage));
        window.history.replaceState({}, '', url);
      }
    };

    previous?.addEventListener('click', () => {
      if (currentPage <= 1) return;
      currentPage -= 1;
      renderPage(true);
      list.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    next?.addEventListener('click', () => {
      if (currentPage >= pageCount) return;
      currentPage += 1;
      renderPage(true);
      list.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    renderPage(requestedPage !== currentPage);
  });

  const refreshPackageButtons = (ready) => {
    document.querySelectorAll('[data-sb-b-package]').forEach(form => {
      const quote = ready ? window.SBStoreB.quote(form.querySelector('[name="variant"], [name="id"]').value) : null;
      const button = form.querySelector('[data-sb-product-submit]');
      button.disabled = !quote?.available;
      button.textContent = quote?.available ? button.dataset.sbReadyLabel : ui('Unavailable');
      form.dataset.sbBReady = String(ready);
      const price = form.closest('.sb-removal-package').querySelector('.sb-display-money');
      if (price) {
        price.textContent = quote?.available ? formatProductMoney(quote.price, currentCurrency()) : ui('Unavailable');
        if (quote?.available) price.dataset.sbEurMinor = String(quote.price);
        else delete price.dataset.sbEurMinor;
      }
    });
  };
  let productAvailabilityRunning = false;
  const refreshProductAvailability = async (force = false) => {
    if (!crossStoreEnabled || productAvailabilityRunning) return;
    productAvailabilityRunning = true;
    const forms = [...document.querySelectorAll('[data-sb-product-root] form')];
    try {
      if (!window.SBStoreB) throw new Error('Availability could not be checked. Please refresh.');
      await window.SBStoreB.refresh(force);
      refreshPackageButtons(true);
      forms.forEach(form => {
        const node = form.querySelector('[data-sb-product-variants]');
        if (!node) return;
        const variants = JSON.parse(node.textContent);
        variants.forEach(variant => { const quote = window.SBStoreB.quote(variant.id); variant.available = quote.available; variant.price = quote.price; variant.compare_at_price = quote.original_price; });
        node.textContent = JSON.stringify(variants);
        const fromPrice = form.querySelector('[data-sb-product-price]');
        if (fromPrice) fromPrice.textContent = formatProductMoney(Math.min(...variants.map(v => v.price)), currentCurrency());
        form.querySelectorAll('[data-sb-linked-addon], [data-sb-gig-variant], [data-sb-priced-addon], [data-sb-direct-variant]').forEach(input => {
          const id = input.dataset.sbLinkedAddon || input.dataset.sbPricedAddon || input.value;
          const quote = window.SBStoreB.quote(id);
          input.disabled = !quote.available;
          input.dataset.sbUnavailable = String(!quote.available);
          input.dataset.sbAddonPrice = String(quote.price);
          input.dataset.price = String(quote.price);
          input.closest('label')?.setAttribute('title', quote.available ? '' : ui('Unavailable'));
        });
        form.dataset.sbBReady = 'true';
        updateProductVariant(form);
        if (form.dataset.sbBError === 'true') { setProductMessage(form, ''); delete form.dataset.sbBError; }
      });
    } catch (error) {
      refreshPackageButtons(false);
      forms.forEach(form => { form.dataset.sbBReady = 'false'; form.dataset.sbBError = 'true'; updateProductVariant(form); setProductMessage(form, error.message, true); });
    } finally { productAvailabilityRunning = false; }
  };
  document.querySelectorAll('[data-sb-product-root] form').forEach((form) => updateProductVariant(form));
  if (crossStoreEnabled) {
    const catalogUrl = new URL(window.location.href);
    if (document.querySelector('.sb-listing-shell') && catalogUrl.searchParams.has('filter.v.availability')) {
      catalogUrl.searchParams.delete('filter.v.availability');
      window.location.replace(catalogUrl.href);
    }
    refreshProductAvailability(true);
    if (document.querySelector('[data-sb-b-cart-page]')) refreshCartDrawer();
    window.addEventListener('focus', () => refreshProductAvailability(true));
    window.addEventListener('storage', event => { if (event.key === window.SBStoreB?.key) refreshCartDrawer(); });
    setInterval(() => { if (document.visibilityState === 'visible') { refreshProductAvailability(true); if (document.querySelector('[data-sb-b-cart-page]') || cartDrawer?.getAttribute('aria-hidden') === 'false') refreshCartDrawer(); } }, 30000);
  }

  const syncCartSurface = () => {
    if (cartDrawer?.getAttribute('aria-hidden') === 'false') refreshCartDrawer();
    else syncCartCount();
  };
  document.addEventListener('sb:display-currency', () => {
    document.querySelectorAll('[data-sb-product-root] form').forEach((form) => updateProductVariant(form));
    if (cartDrawer?.getAttribute('aria-hidden') === 'false') refreshCartDrawer();
  });
  cartChannel?.addEventListener('message', syncCartSurface);
  ['cart:change', 'cart:updated', 'cart:refresh'].forEach((eventName) => {
    document.addEventListener(eventName, syncCartSurface);
  });
  window.addEventListener('pageshow', () => {
    if (!subscriptionsEnabled) document.querySelectorAll('[data-sb-product-root] form').forEach((form) => updateProductVariant(form));
    syncCartCount().then((cart) => { if (cart) notifyCartMutation(); });
  });
  window.addEventListener('focus', syncCartSurface);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') syncCartSurface();
  });
  syncCartCount().then((cart) => { if (cart) notifyCartMutation(); });

  document.querySelectorAll('[data-sb-cookie-preferences]').forEach((button) => button.addEventListener('click', () => {
    if (window.Shopify?.customerPrivacy?.showPreferences) window.Shopify.customerPrivacy.showPreferences();
    else announce('Cookie preferences are unavailable until the approved consent tool is connected.');
  }));
})();

(() => {
  const rootSelector = '[data-sb-review-provider="judge-me"]';
  const interactiveSelector = 'input:not([type="hidden"]),select,textarea,button';
  const fieldsetFor = (control) => control?.closest('.jdgm-form__fieldset') || null;
  const labelFor = (control, fieldset) => {
    if (!control || !fieldset) return null;
    const identifier = control.id;
    return identifier
      ? fieldset.querySelector('label[for="' + CSS.escape(identifier) + '"]')
      : fieldset.querySelector('label');
  };
  const markIdentityTouched = (form) => {
    form.dataset.sbJudgeIdentityTouched = 'true';
  };
  const ownsNode = (owner, node) => Boolean(owner && node && owner.contains(node));
  const moveOwnedBefore = (owner, node, anchor) => {
    if (!ownsNode(owner, node) || !ownsNode(owner, anchor) || node === anchor || node.contains(anchor)) return false;
    const parent = anchor.parentNode;
    if (!parent || !ownsNode(owner, parent)) return false;
    if (node.parentNode === parent && node.nextSibling === anchor) return false;
    parent.insertBefore(node, anchor);
    return true;
  };
  const orderOwnedBefore = (owner, nodes, reference) => {
    if (!ownsNode(owner, reference)) return false;
    const ordered = [...new Set(nodes)].filter((node) => ownsNode(owner, node) && node !== reference);
    let anchor = reference;
    let changed = false;
    for (let index = ordered.length - 1; index >= 0; index -= 1) {
      const node = ordered[index];
      changed = moveOwnedBefore(owner, node, anchor) || changed;
      if (node.parentNode === anchor.parentNode && node.nextSibling === anchor) anchor = node;
    }
    return changed;
  };
  const moveOwnedAfter = (owner, node, reference) => {
    if (!ownsNode(owner, node) || !ownsNode(owner, reference) || node === reference || node.contains(reference)) return false;
    const parent = reference.parentNode;
    if (!parent || !ownsNode(owner, parent)) return false;
    const next = reference.nextSibling;
    if (node.parentNode === parent && node === next) return false;
    if (next) parent.insertBefore(node, next);
    else parent.append(node);
    return true;
  };
  const enhanceForm = (form) => {
    const rating = form.querySelector('input[name="score"]');
    const title = form.querySelector('input[name="review_title"]');
    const body = form.querySelector('textarea[name="review_body"]');
    const name = form.querySelector('input[name="reviewer_name"]');
    const email = form.querySelector('input[name="reviewer_email"]');
    const nameFormat = form.querySelector('select[name="reviewer_name_format"]');
    const media = form.querySelector('input[type="file"]');
    const ratingField = fieldsetFor(rating);
    const titleField = fieldsetFor(title);
    const bodyField = fieldsetFor(body);
    const identityField = fieldsetFor(nameFormat) || fieldsetFor(name);
    const emailField = fieldsetFor(email);
    const mediaField = fieldsetFor(media);
    const actionsField = form.querySelector('.jdgm-form__fieldset-actions');

    ratingField?.classList.add('sb-judge-rating-field');
    titleField?.classList.add('sb-judge-title-field');
    bodyField?.classList.add('sb-judge-body-field');
    identityField?.classList.add('sb-judge-identity-field');
    emailField?.classList.add('sb-judge-email-field');
    mediaField?.classList.add('sb-judge-media-field');
    actionsField?.classList.add('sb-judge-actions-field');

    if (title && titleField) {
      const titleIsRequired = title.required || title.getAttribute('aria-required') === 'true';
      if (!titleIsRequired) {
        titleField.hidden = true;
        titleField.dataset.sbJudgeOptionalTitleHidden = 'true';
      }
    }

    const privacyField = [...form.querySelectorAll('.jdgm-form__fieldset')].find((fieldset) => (
      String(fieldset.textContent || '').toLowerCase().includes('how we use your data')
    ));
    if (privacyField) {
      privacyField.classList.add('sb-judge-privacy-field');
      if (!privacyField.querySelector(interactiveSelector)) {
        privacyField.hidden = true;
        privacyField.dataset.sbJudgeInformationalPrivacyHidden = 'true';
      }
    }

    const nameLabel = labelFor(name, identityField);
    if (nameLabel && nameLabel.textContent !== ui('Name')) nameLabel.textContent = ui('Name');
    const formatContainer = nameFormat?.closest('.jdgm-form__reviewer-name-format-container');
    const formatLabel = labelFor(nameFormat, formatContainer || identityField);
    if (formatLabel && formatLabel.textContent !== 'Public name') formatLabel.textContent = 'Public name';
    if (nameFormat) nameFormat.setAttribute('aria-label', 'Public name format');
    [...(formatContainer?.childNodes || [])].forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE && /^[\s()]*$/.test(node.textContent || '')) node.remove();
    });

    if (bodyField) {
      orderOwnedBefore(form, [ratingField, titleField?.hidden ? null : titleField, identityField, emailField], bodyField);
      moveOwnedAfter(form, mediaField, bodyField);
    }

    if (!form.dataset.sbJudgeIdentityListeners) {
      [name, email, nameFormat].filter(Boolean).forEach((control) => {
        control.addEventListener('input', () => markIdentityTouched(form));
        control.addEventListener('change', () => markIdentityTouched(form));
      });
      form.dataset.sbJudgeIdentityListeners = 'true';
    }

    const identityIsBlank = !String(name?.value || '').trim() && !String(email?.value || '').trim();
    const identityIsEditable = Boolean(
      name && email && nameFormat
      && !name.disabled && !name.readOnly
      && !email.disabled && !email.readOnly
      && !nameFormat.disabled
    );
    const anonymousOption = nameFormat
      ? [...nameFormat.options].find((option) => {
        const value = String(option.value || '').trim().toLowerCase();
        const text = String(option.textContent || '').trim().toLowerCase();
        return value === 'anonymous' || text === 'anonymous';
      })
      : null;
    if (
      identityIsBlank
      && identityIsEditable
      && anonymousOption
      && form.dataset.sbJudgeIdentityTouched !== 'true'
      && form.dataset.sbJudgeAnonymousDefaulted !== 'true'
    ) {
      nameFormat.value = anonymousOption.value;
      form.dataset.sbJudgeAnonymousDefaulted = 'true';
    }

    form.dataset.sbJudgeFormEnhanced = 'true';
  };
  const enhanceReview = (review) => {
    review.dataset.sbReviewEnhanced = 'true';
    const actions = review.querySelector('.jdgm-rev__actions');
    const productTitle = String(review.dataset.productTitle || '').trim();
    if (actions && productTitle && !actions.querySelector('.sb-judge-review-product')) {
      const product = document.createElement('span');
      product.className = 'sb-judge-review-product';
      product.textContent = productTitle.replace(/^Booking(?:[.]com|com)\b/i, 'Booking');
      actions.append(product);
    }
  };
  const enhanceRoot = (root) => {
    root.querySelectorAll('form.jdgm-form').forEach(enhanceForm);
    root.querySelectorAll('.jdgm-rev').forEach(enhanceReview);
  };
  document.querySelectorAll(rootSelector).forEach((root) => {
    let pending = false;
    const observationOptions = { childList: true, subtree: true };
    let observer;
    const run = () => {
      pending = false;
      observer.disconnect();
      try {
        enhanceRoot(root);
      } finally {
        observer.observe(root, observationOptions);
      }
    };
    observer = new MutationObserver(() => {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(run);
    });
    enhanceRoot(root);
    observer.observe(root, observationOptions);
  });
})();

// Restore the conditional field correctly when the browser restores form choices.
function syncWritingInstructions() { document.querySelectorAll('[data-sb-writing-instructions]').forEach(box => { const form=box.closest('form'); const providing=form?.querySelector('[data-sb-content-choice="provide"]')?.checked===true; box.hidden=providing; box.querySelectorAll('textarea').forEach(input=>{input.disabled=providing;}); }); }
window.addEventListener('pageshow',syncWritingInstructions);
document.addEventListener('shopify:section:load',syncWritingInstructions);
syncWritingInstructions();
